import { useLocale } from '@/contexts/LocaleContext'
import type { SiteLocale } from '@/lib/locale'
import { toUsCopyDeep } from '@/lib/us-english'

export type Translations = typeof en

export const en = {
  nav: {
    scribe: 'Scribe',
    enterprise: 'Enterprise',
    switching: 'Switching',
    pricing: 'Pricing',
    aboutUs: 'About us',
    team: 'Team',
    blog: 'Blog',
    logIn: 'Log in',
    lupaOS: 'Lupa OS',
    lupaNotes: 'Lupa Notes',
    contactUs: 'Contact Us',
    closeMenu: 'Close menu',
    openMenu: 'Open menu',
    loginMobile: 'Log in → Lupa OS',
  },
  announcement: {
    label: 'Announcement',
    text: 'New webinar: Learn about upcoming hospitalisation features in Lupa.',
    cta: 'Register now.',
    dismiss: 'Dismiss announcement',
  },
  hero: {
    aiPowered: 'AI Powered',
    heading: 'Veterinary Practice Management Software',
    subtitle: "The World's First Enterprise Ready, AI Operating System for Vets",
    bookDemo: 'Book a demo',
    contactUs: 'Contact us',
  },
  integrations: {
    heading: 'Seamlessly Integrate with Popular Industry Software',
    subheading: 'Integrated with all your essentials',
  },
  coreFeatures: {
    heading1: 'Core Features,',
    heading2: 'Fully Integrated.',
    subheading: 'Everything your clinic needs to succeed - natively integrated and intelligently designed.',
    pms: {
      eyebrow: 'PMS Reimagined',
      title: 'Your PMS - Thoughtfully Upgraded',
      description:
        'Experience an operating system that delivers all the core functionalities - consults, scheduling, records, invoicing - with every detail carefully considered by vets and built by world-class engineers.',
    },
    comms: {
      eyebrow: 'Client Communications',
      title: 'Stay Connected Effortlessly',
      description:
        "Reminders, email campaigns, online booking, chat via the app. Keep clients engaged, connected and loyal.",
    },
    ai: {
      eyebrow: 'AI Note Taking',
      title: 'AI Transcription – Save 60 Minutes Per Day Per Vet',
      description:
        "Transcribe and format your medical notes instantly - from consults to surgical notes and phone calls. Boost accuracy, save time and focus on pet and client care.",
    },
    finance: {
      eyebrow: 'Finance & Analytics',
      title: "Understand Your Clinic's Financial Health",
      description:
        "Actively grow your revenue by tracking performance and spotting growth opportunities. It's never been easier to obtain all the data you need.",
    },
    pullQuote: '"Lupa fills the gap that every other PMS is missing"',
    pullQuoteAuthor: 'Cameron, Founder & Veterinary Surgeon',
    pullQuoteClinic: 'Essex Vets',
  },
  stats: {
    heading1: 'Unrivalled Support.',
    heading2: 'Unmatched care.',
    petsLabel: 'Pets Registered',
    featuresLabel: 'New Features Released in 2025',
    responseLabel: 'Average Customer Response Time',
    minUnit: 'min',
    secondUnit: 's',
  },
  logoCloud: {
    heading: 'Join the Clinics Unlocking Hidden Revenue with Lupa',
  },
  roi: {
    heading: 'Reclaim 10+ hours every week',
    subheading: "With Lupa's intuitive system and tech - free up >£12,000 worth of vet time per year.",
    card1Title: 'Reclaim 10+ hours every week',
    card1Sub: "With Lupa's intuitive system and tech - free up >£12,000 worth of vet time per year.",
    card2Title: '+20% Client Lifetime Value',
    card2Sub: "Lupa's automated booking, follow-ups, and billing result in more visits, stronger loyalty, and increased spend.",
    card3Title: 'Cut Tech Spend by 50%',
    card3Sub: "Replace multiple standalone tools with Lupa's native features - saving over £4,000 per year.",
  },
  testimonials: {
    heading: 'Trusted by Leading Veterinary Clinics',
    prevAriaLabel: 'Previous review',
    nextAriaLabel: 'Next review',
    slides: [
      {
        quote: '"I can read three halters an hour, which means I\'m twice as efficient. So from a business perspective it means we can do what was taking 5 days... comfortably in 3 days".',
        role: 'Director & RCVS Specialist in Cardiology',
      },
      {
        quote: '"When we\'ve had an issue, your support team has been there to make sure they\'ve been fixed regardless of the time of day…which has really set you apart from any other PMS transition that I\'ve had before".',
        role: 'Founder & Director',
      },
      {
        quote: '"You all were outstanding. Your tech guys, my God, they\'re just geniuses. I\'m just in total awe of all of them".',
        role: 'Clinical Director',
      },
      {
        quote: '"Lupa absolutely changed my day-to-day veterinary life, ensuring I had far more time to spend with my patients than my computer!"',
        role: 'Veterinary Surgeon',
      },
    ],
  },
  finalCta: {
    heading: 'The Future of Pet Care Is Ours to Build',
    sub: "Lupa is creating the world's most connected, intelligent, and complete ecosystem for the world's 1bn pets.",
    button: 'Be a part of it.',
  },
  notesFinalCta: {
    heading: "Boost Your Team's Productivity",
    sub: 'Empower your vets to focus on care, not admin.',
    button: 'Try Lupa Notes',
  },
  migrationsFinalCta: {
    heading: 'Ready to move forward?',
    sub: "We understand every clinic is different. That's why we tailor your journey from day 1.",
    button: 'Speak to us',
  },
  pricingFinalCta: {
    heading: 'Invest in Your Business Today',
    button: 'Find out more',
  },
  teamFinalCta: {
    heading: 'Build the Future With the Best',
    sub: 'We ship fast, but never at the cost of resilience. Join us to create technology built to last.',
    button: 'Our Vacancies',
  },
  footer: {
    tagline: 'AI POWERED PETCARE',
    email: 'hello@lupapets.com',
    company: 'Company',
    legal: 'Legal',
    terms: 'Terms & Conditions',
    privacy: 'Privacy Policy',
    dataComplaint: 'Data protection complaint',
    followUs: 'Follow us',
    copyright: '© Lupa Pets 2025 All Rights Reserved',
    appStore: 'Download on the App Store',
    googlePlay: 'Get it on Google Play',
    downloadApp: 'Download app',
  },
  pricing: {
    heroHeading: "Invest in Your Clinic's Success",
    heroSubtitle: 'Explore our pricing plans and learn how much more your clinic could achieve',
    calcHeading: 'Calculate Your Custom Quote',
    calcSub: 'Adjust the settings below to see your personalised quote',
    monthlyCostLabel: 'MONTHLY COST',
    oneTimeSetupLabel: 'ONE-TIME SETUP',
    trainingNote: 'Optional training and data migration packages available depending on your requirements.',
    usersTooltip: 'Number of vets and staff who will actively use Lupa.',
    sites: 'Sites',
    users: 'Users',
    sitesLabel: 'Number of clinic sites',
    usersLabel: 'Number of Lupa users',
    sitePrice: '£100/month per site',
    userPrice: '£50/user per month',
    notesPrice: '£35/user per month',
    notesDiscount: '100% off (limited time offer)',
    total: 'Total monthly',
    totalRecurring: 'Total monthly recurring fee',
    investHeading: 'Your Investment',
    investSub: 'Transparent pricing with no hidden fees',
    setupNote: 'ONE-TIME SETUP - contact us for pricing',
    makeSwitch: 'Make the switch',
    bookDemo: 'Book a demo',
    customPlan: 'Contact us for a custom plan',
    contactUsBtn: 'Contact us',
    moreThanSites: 'More than 5 sites? Speak to our team for a custom plan.',
    moreThanUsers: 'More than 30 users? Speak to our team for a custom plan.',
    noteFree: 'FREE (100% off)',
    perMonth: '/mo',
    decreaseSites: 'Decrease Sites',
    increaseSites: 'Increase Sites',
    decreaseUsers: 'Decrease Users',
    increaseUsers: 'Increase Users',
    roiHeading: 'Explore New Revenue Opportunities with Lupa',
    roiSub: 'See the difference Lupa makes for a single site, 3 vet clinic.',
    notesCard: {
      title: 'Lupa Notes',
      revenue: '£6,000+/mo',
      revenueLabel: 'Projected revenue gain per month',
      assumptions: 'Assumptions',
      benefits: 'Benefits',
      assumption1: 'Saves 60–90 minutes per consulting day',
      assumption2: 'Vets consult 3–4 days/week',
      assumption3: 'Avg. consult duration 15–30 minutes',
      assumption4: 'Avg. consult price £60',
      benefit1: 'Saves up to 5 hours per vet each week',
      benefit2: 'Creates time for 10+ extra consults or 3+ surgeries per vet/week',
    },
    reminderCard: {
      title: 'Reminder System',
      revenue: '£8,000+/mo',
      assumption1: 'Avg 180 appointments booked per week currently',
      assumption2: '20% increase in weekly consults/ops booked from improved client engagement',
      assumption3: 'Avg. appointment price £60',
      benefit1: 'Book up to 36 appointments per week with reminders and CRM',
      benefit2: '144 additional appointments each month',
    },
    chargeCard: {
      title: 'Charge Capture',
      revenue: '£2,000+/mo',
      assumption1: 'Total invoice value per month is £50,400 for single site, 3 vet clinic',
      benefit1: '5% improvement in capturing missed charges',
      benefit2: 'Helps recover lost revenue through accurate and assisted billing',
    },
    testimonialQuote: '"It\'s life changing… saving me 5–10 minutes in every single consult, do that 10–20 times a day"',
    testimonialAttr: 'Paul Irvine-Smith - Veterinary Surgeon and Clinical Director, Witterings Vets',
    testimonialName: 'Paul Irvine-Smith',
    testimonialRole: 'Veterinary Surgeon and Clinical Director, Witterings Vets',
    diagramHeading: 'Halve your spend, double your output',
    diagramSub: 'Trade your patchwork of stand-alone tools for an ecosystem built to work in sync.',
    enterpriseHeading: 'Enterprise or Larger Group?',
    enterpriseCta: 'Contact us',
    enterpriseDesc: 'Get in touch to discuss custom pricing, larger-scale features, and dedicated support - from onboarding to getting the most out of Lupa.',
    logoCloudHeading: 'Join the clinics already benefiting from the switch',
  },
  enterprise: {
    heroHeading1: 'Veterinary Operating',
    heroHeading2: 'System for Enterprises',
    heroSubtitle:
      'The world\'s first enterprise solution for veterinary groups - from centralised control to configurations at individual clinic level',
    heroCta: 'Your custom plan →',
    responseLabel: 'Response time',
    responseUnit: 'mins',
    ratingLabel: 'Capterra Rating',
    uptimeLabel: 'Guaranteed Uptime',
    featHeading: 'Optimised for Growth, Security, and Success',
    featSub: 'Giving your team the support, tools, and protection they need to succeed',
    support: {
      title: 'Enterprise Support',
      b1: 'Dedicated enterprise success team & account manager.',
      b2: 'Human + AI support available 24/7.',
      b3: 'Tailored onboarding plans for large groups.',
      b4: 'Best-in-Class SLAs with guaranteed response times.',
    },
    growth: {
      title: 'Made for Growth',
      b1: 'Create and set up new branches within minutes with pre-configured settings for services, pricing, and workflows.',
      b2: 'AI-enabled data transfer ensures accurate migration of existing clinic records, completed within 12 hours.',
      b3: 'Ability to brand individual clinics.',
      b4: 'Easily expand across regions with centralised templates and configurations.',
    },
    security: {
      title: 'Bank-Grade Security',
      b1: 'SOC 2 Type II certified and GDPR compliant.',
      b2: 'Full audit trails for enterprise compliance.',
      b3: 'Single Sign-On (SSO) and role-based access.',
      b4: 'Banking-grade encryption (TLS 1.2 in transit, AES 256-bit at rest).',
      link: 'More on security',
    },
    hierarchyHeading: 'Managing Enterprise Complexity, with Ease',
    hierarchySub:
      'Establish consistency centrally while choosing where flexibility applies across groups, clinics, and departments.',
    tabs: {
      enterprise: 'Enterprise Level',
      regional: 'Regional Level',
      clinic: 'Clinic Level',
      department: 'Department Level',
    },
    enterpriseTab: {
      description:
        'The highest level that oversees all regions, setting group-wide guidelines, standards, and controls. System supports as many layers as needed.',
      b1: 'Reference services, inventory lists, and pricing are standardised across all sites to maintain consistency',
      b2: 'Enterprise-wide updates can be rolled out instantly to every clinic in the network',
      b3: 'Global rules for billing, discounts, and protocols can be defined centrally',
      b4: 'Compliance, security, and governance standards are applied globally to protect operations',
    },
    regionalTab: {
      description: 'Large geographies or branches, co-ordinating multiple clinics within the same area or brand.',
      b1: 'Regional managers can access financial, operational, and clinical KPIs across all clinics to benchmark and support decision-making',
      b2: 'Clinics in the same region can share clinical and client data for smoother referrals and continuity of care',
      b3: 'Service lists, stock levels, and permissions can be adjusted instantly across all clinics in the group',
      b4: 'Third-party tools can be integrated once and used across the entire region',
    },
    clinicTab: {
      description: 'The individual veterinary site, where the team can customise workflows within set parameters.',
      b1: 'HQ defines the permissions framework, which sets the level of autonomy each clinic has to customise operations',
      b2: 'Clinics can adjust stock lists, service offerings, staff rotas, communication methods and frequency, within HQ-defined limits',
      b3: 'Client data and medical records are accessible and synchronised within and optionally across clinics',
      b4: 'Languages, currencies, and tax settings can be defined centrally and applied to individual sites',
    },
    departmentTab: {
      description:
        'Departments within a clinic (e.g. referral teams, day/night shifts) allowing deeper customisation for specific roles or functions.',
      b1: 'Departments can create their own services, appointment types, and templates',
      b2: 'Workflows, reminders, and documentation can be customised for specific teams',
      b3: 'Permissions and access levels can be set differently for each department',
      b4: 'Team-specific diary management, scheduling, and online booking can be enabled',
    },
    readyHeading: 'Run your group like one practice.',
    readySub:
      'Custom pricing, larger-scale features, and a dedicated team - from onboarding through to your hundredth clinic.',
    readyCta1: 'Customise your quote →',
    readyCta2: 'See all features →',
    readyBadge: '+ GROUP PLAN',
    readySpecialist: 'Specialist support & account manager',
    readySetup: 'Fast and easy set-up - clinics live in 12 hours',
    readyControl: 'Central control across every branch',
    readySecurity: 'Bank-grade security · SOC 2 · GDPR',
  },
  migrations: {
    heroHeading: 'Switch to Lupa',
    heroSubtitle: 'Lupa handles your onboarding with speed, accuracy, and full support – making change feel easy.',
    heroCta: 'Find out more',
    reliabilityHeading: 'The Most Reliable Data Migration on the Market',
    reliabilitySub: 'Complete, fast and executed with careful precision - trust our expert team with your data.',
    complete: {
      title: 'Complete Migration',
      body: "We go the extra mile from day one. As the only PMS that transfers all financial and medical records, we make sure nothing gets left behind during your upgrade.",
    },
    accuracy: {
      title: 'Accuracy',
      body: "Reviewed by expert engineers and enhanced by 100+ checkpoints. We set the industry benchmark in thoroughness and reliability.",
    },
    experience: {
      title: 'Experience',
      body: "We only hire the best. Our data engineers have years of experience in the veterinary space and understand your data.",
    },
    integrationsHeading: 'Integrate with tools you love',
    integrationsSub: 'Integrated with all your essentials - right out of the box.',
    testimonialsHeading: 'Migrations that Speak for Themselves',
    prevAriaLabel: 'Previous migration testimonial',
    nextAriaLabel: 'Next migration testimonial',
    migrationQuotes: [
      "Nothing's ever too much hassle, they've always been there for us to help",
      "We have had amazing support from you guys...all the training that we did beforehand made a huge difference",
      "It's certainly been one of the easiest and smoothest transitions that we've had…I've been through a couple in my career",
      "As integrations go, Lupa has been the best yet and the support from you and your team has been amazing",
      "One of my major concerns was around data transfer…the engineers have been really helpful",
    ],
    gdprCompliantLabel: 'COMPLIANT',
    securityHeading: 'Your Data, Protected',
    securityTab1: 'Data Security & Privacy',
    securityTab2: 'Infrastructure & Reliability',
    securityTab3: 'GDPR',
    securityBody1:
      "At Lupa, protecting your clinic's data is non-negotiable. We're SOC 2 and GDPR compliant, with banking-grade encryption (TLS 1.2 in transit, AES 256-bit at rest). Admin-level controls and detailed audit logs ensure only the right people access the right data.",
    securityBody2:
      "Your data is backed up every 2 minutes and hosted in your region on secure, scalable AWS infrastructure - the same trusted platform used by leading global organisations. We prioritise reliability at every level.",
    securityBody3:
      "Lupa adheres to leading international privacy standards, including GDPR, so our systems remain compliant across most geographies. Your clinic's data stays secure, private, and compliant - wherever you operate.",
    securityBtn: 'Our Security Measures',
    onboardingHeading: 'Lupa Onboarding, Step by Step',
    onboardingSub: 'Our specialist team supports you step by step, at your speed.',
    steps: {
      planning: {
        title: 'Planning',
        duration: '1 day',
        body: "Meet your dedicated onboarding advisor. We learn your clinic's workflows, values and team dynamics to tailor Lupa to fit you.",
      },
      config: {
        title: 'Configuration',
        duration: '4-8 weeks',
        body: "We'll clean your data, set up your system, and guide integrations with your trusted partners to fit Lupa to your workflow.",
      },
      training: {
        title: 'Training',
        duration: '2-4 weeks',
        body: "Your team will receive comprehensive training via our sandbox environment, guided sessions and self-paced exercises. We make your success our responsibility.",
      },
      golive: {
        title: 'Go Live Support',
        duration: '2 days (migration clinics)',
        body: 'Your advisor will be there in person on Go Live day, to support you and your team ensuring a smooth start with minimal stress.',
      },
      hypercare: {
        title: 'Hypercare',
        duration: '3 weeks',
        body: 'Most learning happens in practice. For three weeks after launch, we provide hands-on support to help your team gain confidence.',
      },
      ongoing: {
        title: 'Ongoing Support',
        duration: 'Indefinitely',
        body: "Our Customer Success team checks in proactively, tracks your goals, and ensures Lupa evolves with your needs - backed by a 2m 35s response time and 98.2% client satisfaction score.",
      },
    },
    faqHeading: 'Your Questions Answered',
    faqSub: "Need more info?\nJust drop us a message and we'll get back to you.",
    faq: [
      {
        question: 'Will someone be with me on go-live day?',
        answer:
          "For migrating clinics, yes - your dedicated onboarding advisor will be there in person on go-live day to support you and your team. For new/startup practices, on-site go-live support isn't included as standard since there is less going on on day 1, but you can opt for this as an additional paid service. Either way, every clinic receives the same comprehensive training, pre-launch support, and access to our team on the day so you're never left on your own.",
      },
      {
        question: 'Who does Lupa integrate with?',
        answer:
          'Lupa integrates with all the key partners a veterinary practice relies on. For laboratory diagnostics, we connect with IDEXX and Zoetis for both in-house and external lab machines. For pharmacy ordering, we integrate with NVS and MWI. For insurance claims, we work with Vetenvoy. We also support PACS integration for imaging, along with Stripe, Dojo, Outlook, Gmail, and more.',
      },
      {
        question: 'What is an open API and what does it mean for my practice?',
        answer:
          "An open API is a way for different software systems to talk to each other. In practice, it means Lupa can connect with tools and services beyond our existing integrations - whether that's a referral platform, an accounting package, or something specific to how your clinic operates.",
      },
      {
        question: 'How long does it take to switch to Lupa?',
        answer:
          'The full onboarding process typically takes around 6–12 weeks from start to finish. That includes a planning session, 4–8 weeks of data migration and system configuration, 2–4 weeks of team training, and then go-live with hands-on hypercare support for 3 weeks afterwards.',
      },
      {
        question: 'Will I lose any data when switching from my current PMS?',
        answer:
          'No. Lupa is the only PMS that transfers all financial and medical records during migration. Our specialist data engineers review every migration through 100+ quality checkpoints to ensure nothing is lost or corrupted.',
      },
      {
        question: 'What support do I get after go-live?',
        answer:
          "You're not left to figure things out alone. After launch, you get 3 weeks of hypercare - hands-on support while your team builds confidence with the system in a real clinical setting. After that, our Customer Success team checks in proactively and our support team provides ongoing support with an average response time of under 3 minutes and a 98.2% client satisfaction score.",
      },
      {
        question: 'Can I switch to Lupa from any practice management system?',
        answer:
          "Yes. Lupa's migration team has experience transferring data from a wide range of veterinary PMS platforms. Our data engineers specialise in veterinary data and understand the nuances of different systems.",
      },
    ],
  },
  notes: {
    heroHeading1: 'Transcribe & Summarise Records',
    heroHeading2: 'Effortlessly with Lupa Notes',
    heroSubPre: 'Your AI investment that generates an additional ',
    heroSubHighlight: '£27,000 per vet per year',
    heroSubPost: ', and noticeably boosts staff happiness and productivity.',
    heroCta: 'Contact Us',
    stepsHeading: 'Lupa Notes is the Future of Veterinary Care',
    stepsSub: 'Our aim is to permanently reshape the vet industry by empowering busy vets with easy to use tools.',
    steps: [
      {
        label: 'Record',
        description:
          'Capture every moment of the consult - from start to finish on your phone or computer. Stay fully focused on your patient and client, while your consult is transcribed in the background.',
      },
      {
        label: 'Summarise',
        description:
          'We filter out the small talk, turning every conversation into clear, concise medical notes. Choose a template or create your own - and Lupa will format it for you instantly.',
      },
      {
        label: 'Action List',
        description:
          'Never lose track of what needs doing. Automatically create actionable task lists - from follow-up calls to arranging a referral. Stay organised and deliver on every promise.',
      },
      {
        label: 'Enhance',
        description:
          'Need to include more? Add voice notes, upload referral letters, medical history or lab results with your recording. Lupa incorporates key medical documents into your summaries to make them complete.',
      },
      {
        label: 'Export',
        description:
          'Revise your notes manually or with the help of our AI agent, Jerry, before uploading to your PMS. Finally create client-friendly summaries for your clients to take home, all with one click.',
      },
    ],
    benefitsHeading1: 'One Simple Switch,',
    benefitsHeading2: 'Countless Benefits',
    benefitsSub: 'Thousands of vets are saving 60 minutes per day with Lupa Notes. You can too.',
    benefitsCta: 'Book a free trial',
    benefit1Title: 'Save 60 Minutes Per Vet Daily',
    benefit1Desc: 'Free up time for ops, reporting, and patient care by automating time-consuming, manual admin tasks.',
    benefit2Title: 'Better Client Experience & Loyalty',
    benefit2Desc:
      'Spend less time typing and more time engaging with clients. Create consult summaries to keep them informed and aligned with the plan.',
    benefit3Title: 'Capture More Revenue, Effortlessly',
    benefit3Desc:
      "Increase revenue by freeing up time to offer add-ons like nail clips and blood tests. Lupa's AI will flag missed billing items directly from your notes.",
    worksWith: 'Use Lupa Notes your way',
    worksWithOption1Title: 'Part of the Package',
    worksWithOption1Desc: 'Lupa Notes is already built into your Lupa OS subscription.',
    worksWithOption2Title: 'Works With Any PMS',
    worksWithOption2Desc:
      'Not using Lupa OS yet? No problem. Lupa Notes works seamlessly with any PMS - start saving time straight away.',
    testimonialsHeading1: 'Trusted by clinics',
    testimonialsHeading2: 'across the industry',
    prevAriaLabel: 'Previous testimonial',
    nextAriaLabel: 'Next testimonial',
    notesTestimonials: [
      {
        designation: 'Veterinary Surgeon, Woodstock',
        quote: '"I think it saves me roughly an hour a day on a normal day. On a busier day, it would save me more... it means clients aren\'t kept waiting, which increases NPS scores, which are really important at the moment."',
      },
      {
        designation: 'Director, Witterings Vets',
        quote: '"It\'s life changing… just my enjoyment of being a vet, going back to caring for my patients, and not writing clinical notes."',
      },
      {
        designation: 'Practice Owner, Burghley Veterinary Centre',
        quote: '"Your AI transcription tool is a complete game changer for us! I can\'t believe how rapidly it is transforming and improving!"',
      },
      {
        designation: 'Veterinary Surgeon, Derbyshire',
        quote: '"I\'m still engaged and focusing on what people are telling me, but it gives me comfort and reassurance that even if I don\'t remember everything or fully process it, it\'s still going to end up in the transcript and in the notes."',
      },
      {
        designation: 'Veterinary Surgeon, Coventry',
        quote: '"It\'s a no brainer. I mean, it\'s quite embarrassing actually how much time I\'m shaving out of a day…I can now do consults in 10 minutes again instead of 15."',
      },
      {
        designation: 'Veterinary Director, Pet People',
        quote: '"My personal experience is this is amazing. I enjoy consultations much more. I\'m not thinking about remembering stuff to write down and not breaking off to write down important information."',
      },
    ],
    faqHeading: 'Your Questions Answered',
    faq: [
      {
        question: 'Is my data safe with Lupa Notes?',
        answer:
          "Yes. Lupa Notes is built with data security and privacy at its core. We are GDPR compliant, SOC 2 Type II certified, and use TLS 1.2 in transit and AES 256-bit encryption at rest. Your recordings and notes are stored securely and are never shared with third parties.",
      },
      {
        question: 'Do I need to get client consent before recording a consultation?',
        answer:
          "Yes, you should always ask your client for consent before starting a recording. We make this simple with ready-made consent workflows and in-clinic signage templates. Most clients are happy once they understand the benefit.",
      },
      {
        question: 'Does Lupa Notes store the audio from my consultations?',
        answer:
          "No, Lupa Notes does not permanently store your audio recordings. Audio is retained temporarily for processing and accuracy checks, then deleted. Only the structured text notes and summaries are kept in your records.",
      },
      {
        question: 'How much time does Lupa Notes actually save vets?',
        answer:
          "Vets using Lupa Notes save an average of 60 minutes per day on clinical note-taking and admin. Over a year, for a clinic with 3 vets, that translates to significant time recovered - time that can be reinvested in consultations, surgeries, or simply leaving on time.",
      },
      {
        question: 'Are the AI-generated notes accurate?',
        answer:
          "Yes - but you're always in control. Lupa Notes captures the full consultation transcript and generates structured notes based on your template. Before anything is saved to the patient record, you review and approve the notes.",
      },
      {
        question: 'Can I customise the format of the notes Lupa Notes generates?',
        answer:
          "Yes. Lupa Notes lets you choose from existing note templates or create your own custom format. You can have different templates per species, consultation type, or clinical team - giving you full control over how your records are structured.",
      },
    ],
  },
  team: {
    heroHeading1: 'Help Us Build the',
    heroHeading2: "World's Petcare Platform",
    heroSubtitle: 'Join Lupa and Leave Your Print on Veterinary Care Worldwide',
    heroCta: 'Join our team',
    europeHeading: "Europe's #1 start-up team",
    europeSub: 'Our people come from the best technology and strategy organisations in the world.',
    valuesHeading: 'The values that define us',
    valuesSub: 'The foundations of our company and culture.',
    values: [
      { title: 'Trust your gut', desc: 'We trust you to lead with confidence, act on intuition and experience, and own your decisions.' },
      { title: 'Fall in love, everyday', desc: 'We encourage you to shape your role, so you find meaning and fulfilment in your work, everyday.' },
      { title: 'Own it like it\'s yours', desc: 'We believe in true ownership - holding yourself accountable, delivering with pride and knowing your effort is recognised and shapes our collective success.' },
      { title: 'Act with extreme urgency', desc: 'We move quickly and carefully, with purpose. Every decision and action drives real impact in this company.' },
      { title: 'Be bold and build the future', desc: 'At Lupa, we encourage bold thinking and fresh ideas - we push boundaries to build solutions.' },
    ],
    gridHeading: 'The Team Behind Lupa',
    gridSub: 'Meet some of the faces shaping the product.',
    formerly: 'Formerly',
    headcountHeading: 'Small Headcount, Big Impact',
    headcountSub: 'Join our carefully chosen team, entrusted and backed to build something impactful.',
    eliteEngineers: 'Elite Engineers',
    raised: 'Raised to date',
    petsRegistered: 'Pets Registered',
    softwareTab: 'Software Engineers',
    dataTab: 'Data Engineers',
    productTab: 'Product Team',
    clientTab: 'Client Team',
    showLess: 'Show Less',
    showAll: 'Show All',
    principlesEyebrow: 'Engineering',
    principlesHeading1: 'Principles That Drive Our',
    principlesHeading2: 'Engineering Team',
    principlesSub: "We've built our engineering culture on the most important lessons our team learned at the world's top tech companies.",
    principles: [
      {
        principle: 'Build to Scale',
        quote: 'We build systems to last. We value strong foundations, meaningful testing, and smart tech choices that keep us fast today and ready for tomorrow.',
      },
      {
        principle: 'Ship Fast',
        quote: 'We move quickly to bring ideas to life. Engineers build, ship, and see their work make an immediate impact.',
      },
      {
        principle: 'Autonomy',
        quote: 'End-to-end ownership is core to how we work. At Lupa, solving real-life problems starts with autonomy and is strengthened by collaboration.',
      },
      {
        principle: 'Customer Obsessed',
        quote: 'We stay in close contact with the clinics - their input guides what we build and how we refine it.',
      },
      {
        principle: 'Hiring the Best',
        quote: 'We believe in the benefits of building with elite talent. We raise the bar deliberately, because working with the best is how you grow faster and build better.',
      },
    ],
    techHeading: 'Our Cutting-Edge Tech Stack',
    techFrontend: 'Frontend',
    techBackend: 'Backend',
    techDatabase: 'Database',
    techDataAnalytics: 'Data & Analytics',
    techMobile: 'Mobile App',
    benefitsHeading: 'Benefits you get with Lupa',
    benefitsSub: 'We invest in our team, so they can be invested in Lupa (literally!)',
    benefits: [
      { title: 'Exceptional compensation', desc: 'Top-of-market salary package designed to attract and retain the best talent.' },
      { title: 'Equity for all', desc: 'Every employee gets equity, no exceptions - share the upside as we grow.' },
      { title: '25 days leave', desc: '25 days annual leave to rest, recharge and explore the world.' },
      { title: 'Visa / relocation', desc: 'Sponsored visa with a full relocation package to get you set up in London.' },
      { title: 'Private Medical', desc: 'Top-tier private medical insurance for you and your peace of mind.' },
      { title: 'MacBook Air M4', desc: 'Latest MacBook Air M4 plus the productivity and AI tools you need.' },
      { title: 'Annual team trip', desc: 'Annual team trip abroad and fortnightly socials - we work hard, play hard.' },
      { title: 'Central London office', desc: 'High-end office in the heart of central London, designed for focus and collaboration.' },
      { title: 'Free coffee & beer', desc: 'Free barista coffee and beer on tap to keep the energy flowing.' },
      { title: 'Subsidised gym', desc: 'Subsidised gym membership so staying fit fits into your day.' },
    ],
  },
  contact: {
    tabDemo: '📋 Book a demo',
    tabMessage: '✏️ Send a message',
    demoHeading: '🚀 See Lupa in action',
    demoSub: 'Book a demo to see how Lupa can transform your clinic.',
    demoQuote: "Every person we've met at Lupa has been amazing - supportive, helpful, and impressively bright.",
    demoQuoteAttr: '- Natasha, Polden Hills Veterinary Centre',
    msgHeading: '🚀 We\'re here to help',
    msgSub: "Have questions? Drop us a message and we'll get back to you within 4 working hours.",
    msgQuote: "Lupa has been the best yet, and the support from you and the team has been amazing!",
    msgQuoteAttr: '- Vet Surgeon, Norwich',
    trustedBy: 'Trusted by clinics globally',
    namePlaceholder: 'Your full name',
    nameLabel: 'Name',
    emailLabel: 'Email',
    emailPlaceholder: 'you@yourclinic.com',
    companyLabel: 'Company Name',
    companyPlaceholder: 'Your clinic or practice',
    locationLabel: 'Where are you based?',
    locationSelect: 'Select...',
    submitBtn: 'Submit',
    successTitle: 'Request received!',
    successSub: 'Our team will reach out within 4 working hours.',
    fullName: 'Full name*',
    emailAddr: 'Email address*',
    phone: 'Phone number',
    company: 'Company',
    message: 'Message',
    privacyLabel: 'I have read and accept the ',
    privacyLink: 'Privacy Policy*',
    termsLabel: ' and ',
    termsLink: 'Terms & Conditions*',
    sendBtn: 'Send message',
    msgSuccessTitle: 'Message sent!',
    msgSuccessSub: "We'll get back to you within 4 working hours.",
    requiredField: 'This field is required',
    privacyRequired: 'Please accept the Privacy Policy and Terms',
  },
  blog: {
    heroHeading: 'The Lupa Blog',
    heroSub: 'Insights on veterinary practice management, AI, and growing your clinic',
    latest: 'All',
    allCategories: 'All',
    readMore: 'Read more',
    techAI: 'Tech & AI',
    lupaNews: 'Lupa News',
    businessInsights: 'Business Insights',
    videos: 'Videos',
    backToBlog: 'Back to Blog',
    noPostsFound: 'No posts found.',
    jumpToSection: 'Jump to section',
    shareArticle: 'Share this article',
    joinMailing: 'Join our mailing list',
    mailingDesc: 'Keep up to date with Lupa news and special offers.',
    emailPlaceholder: 'Your email address',
    subscribe: 'Subscribe',
    seeLupa: 'See Lupa in action',
    book30min: 'Book a 30-min demo with our team.',
    bookDemo: 'Book a demo',
    latestBlogs: 'Latest Blogs',
    backToAll: 'Back to all posts',
    inlineCta: 'Ready to save 60+ min per vet per day?',
    inlineSub1: 'Lupa helps ',
    inlineSub2: 'your practice',
    inlineSub3: ' evolve on ',
    inlineSub4: 'every front',
    letsTalk: "Let's talk",
    // Blog hub + post chrome (previously hardcoded English in the layouts).
    hubHeading: 'Blogs & Success Stories',
    hubLede: 'Practical insights for vet practice owners and managers.',
    featuredLabel: 'Featured article',
    allArticlesLabel: 'All articles',
    readArticle: 'Read article',
    previous: 'Previous',
    next: 'Next',
    pageLabel: 'Page',
    paginationLabel: 'Pagination',
    keyPoints: 'Key points',
    breadcrumbBlog: 'Blog',
    clinicalReview: 'Clinical review',
    clinicallyReviewed: 'Clinically reviewed',
    faqHeading: 'Frequently asked questions',
    writtenBy: 'Written by',
    readMoreAbout: 'Read more about',
    keepReading: 'Keep reading',
    aboutAuthor: 'About %s',
    articlesBy: 'Articles by %s',
  },
  cma: {
    heading: 'Protect Your Business and Team Ahead of the CMA Regulations',
    sub: 'Missed our webinar? Watch the recording - free until November 30th',
    watchBtn: 'Watch it here',
  },
  meta: {
    home: {
      title: 'AI Veterinary Practice Management Software | Lupa Pets',
      description:
        'Lupa is the enterprise-ready AI operating system for veterinary practice management. Automate admin, improve care, and save hours every week.',
    },
    pricing: {
      title: 'Lupa Pets Pricing | Veterinary Practice Management Software',
      description:
        'Transparent pricing for veterinary practices of all sizes. Build your custom Lupa plan and see your projected ROI before you commit.',
    },
    lupaNotes: {
      title: 'AI Veterinary Scribe for Faster Consultations | Lupa Pets',
      description:
        'Lupa Notes transcribes and summarises consultations automatically, saving vets up to 60 minutes per day. The AI scribe built for veterinary practice.',
    },
    migrations: {
      title: 'Switch Veterinary PMS to Lupa | Quick and Easy Migration',
      description:
        'Switch to Lupa with zero downtime. Our migration team handles full data transfer from your existing PMS, with dedicated support throughout. SOC 2 compliant.',
    },
    contact: {
      title: 'Contact Lupa Pets | Book a Demo or Get in Touch',
      description:
        'Book a demo or get in touch with the Lupa team. See how AI practice management software can save your vets an hour or more every day.',
    },
    team: {
      title: 'Meet the Lupa Pets Team | Veterinary Software Experts',
      description:
        'Meet the team behind Lupa. Engineers from Google, Meta, Palantir, and Amazon, building veterinary software that puts independent practices first.',
    },
    enterprise: {
      title: 'Enterprise Veterinary Practice Management | Lupa Pets',
      description:
        'Lupa Enterprise gives multi-site veterinary groups centralised control, consolidated reporting, and AI tools across every location. Built for scale.',
    },
    aaha: {
      title: 'AAHA Advantage - Lupa Pets',
      description:
        'The PIMS built for AAHA-Accredited practices. Preferred pricing, AI-powered workflows and clinical excellence - exclusively for AAHA members.',
    },
    blog: {
      title: 'Veterinary Practice Insights and Guides | Lupa Pets Blog',
      description:
        'Guides, case studies, and expert insight on veterinary practice management, AI technology, CMA regulations, and growing an independent practice.',
    },
    privacyPolicy: {
      title: 'Privacy Policy | Lupa Pets',
      description:
        'Read the Lupa Pets privacy policy to understand how we collect, use, and protect your data. SOC 2 compliant and GDPR ready.',
    },
    dataProtectionComplaints: {
      title: 'Data Protection Complaints Procedure | Lupa Pets',
      description:
        'How to submit a data protection complaint to Lupa Pets, and how we receive, assess, investigate and respond to complaints under UK data protection law.',
    },
    terms: {
      title: 'Terms and Conditions | Lupa Pets',
      description:
        'Read the Lupa Pets terms and conditions governing use of our veterinary practice management software and associated services.',
    },
  },
}

// UK → US copy (spelling plus PMS → PIMS) lives in us-english.ts so the blog
// and the shared nav/footer use exactly the same rules as these strings.
const enAm = toUsCopyDeep(en)

export const us: Translations = {
  ...enAm,
  roi: {
    ...enAm.roi,
    subheading: "With Lupa's intuitive system and tech - free up >$12,000 worth of vet time per year.",
    card1Title: 'Reclaim 10+ hours every week',
    card1Sub: "With Lupa's intuitive system and tech - free up >$12,000 worth of vet time per year.",
    card3Sub: "Replace multiple standalone tools with Lupa's native features - saving over $4,000 per year.",
  },
  notes: {
    ...enAm.notes,
    heroSubPre: 'Your AI investment that generates an additional ',
    heroSubHighlight: '$27,000 per vet per year',
    heroSubPost: ', and noticeably boosts staff happiness and productivity.',
  },
  pricing: {
    ...enAm.pricing,
    sitePrice: '$130/month per site',
    userPrice: '$65/user per month',
    notesPrice: '$35/user',
    notesCard: {
      ...enAm.pricing.notesCard,
      revenue: '$6,000+/mo',
      assumption4: 'Avg. consult price $60',
      benefit2: 'Creates time for 10+ extra consults or 3+ surgeries per vet/week',
    },
    reminderCard: {
      ...enAm.pricing.reminderCard,
      revenue: '$8,000+/mo',
      assumption3: 'Avg. appointment price $60',
    },
    chargeCard: {
      ...enAm.pricing.chargeCard,
      revenue: '$2,000+/mo',
      assumption1: 'Total invoice value per month is $50,400 for single site, 3 vet clinic',
    },
  },
}

export const es: Translations = {
  nav: {
    scribe: 'Notas IA',
    enterprise: 'Empresas',
    switching: 'Cambio',
    pricing: 'Precios',
    aboutUs: 'Sobre nosotros',
    team: 'Equipo',
    blog: 'Blog',
    logIn: 'Iniciar sesión',
    lupaOS: 'Lupa OS',
    lupaNotes: 'Lupa Notes',
    contactUs: 'Contacto',
    closeMenu: 'Cerrar menú',
    openMenu: 'Abrir menú',
    loginMobile: 'Iniciar sesión → Lupa OS',
  },
  announcement: {
    label: 'Anuncio',
    text: 'Nuevo seminario web: descubre las próximas funciones de hospitalización en Lupa.',
    cta: 'Regístrate ahora.',
    dismiss: 'Cerrar anuncio',
  },
  hero: {
    aiPowered: 'Impulsado por IA',
    heading: 'Software de Gestión para Clínicas Veterinarias',
    subtitle: 'El primer sistema operativo de IA para veterinarios listo para empresas',
    bookDemo: 'Reservar demo',
    contactUs: 'Contáctanos',
  },
  integrations: {
    heading: 'Integración perfecta con el software líder del sector',
    subheading: 'Integrado con todo lo esencial',
  },
  coreFeatures: {
    heading1: 'Funcionalidades clave,',
    heading2: 'totalmente integradas.',
    subheading: 'Todo lo que tu clínica necesita para tener éxito - integrado de forma nativa y diseñado de manera inteligente.',
    pms: {
      eyebrow: 'PMS reinventado',
      title: 'Tu PMS - mejorado con criterio',
      description:
        'Experimenta un sistema operativo con todas las funcionalidades clave - consultas, agenda, historiales, facturación - pensado por veterinarios y desarrollado por ingenieros de primer nivel.',
    },
    comms: {
      eyebrow: 'Comunicación con clientes',
      title: 'Mantente conectado sin esfuerzo',
      description:
        'Recordatorios, campañas de email, reservas online, chat desde la app. Mantén a tus clientes comprometidos, conectados y fieles.',
    },
    ai: {
      eyebrow: 'Notas con IA',
      title: 'Transcripción IA - Ahorra 60 minutos al día por veterinario',
      description:
        'Transcribe y formatea tus notas médicas al instante - desde consultas hasta informes quirúrgicos y llamadas. Mayor precisión, menos tiempo, más atención al paciente y al cliente.',
    },
    finance: {
      eyebrow: 'Finanzas y analítica',
      title: 'Comprende la salud financiera de tu clínica',
      description:
        'Impulsa tus ingresos siguiendo el rendimiento y detectando oportunidades de crecimiento. Accede a todos los datos que necesitas como nunca antes.',
    },
    pullQuote: '"Lupa cubre el hueco que ningún otro PMS consigue llenar"',
    pullQuoteAuthor: 'Cameron, Fundador y Cirujano Veterinario',
    pullQuoteClinic: 'Essex Vets',
  },
  stats: {
    heading1: 'Soporte sin rival.',
    heading2: 'Atención incomparable.',
    petsLabel: 'Mascotas registradas',
    featuresLabel: 'Nuevas funciones lanzadas en 2025',
    responseLabel: 'Tiempo de respuesta medio',
    minUnit: 'min',
    secondUnit: 's',
  },
  logoCloud: {
    heading: 'Únete a las clínicas que descubren ingresos ocultos con Lupa',
  },
  roi: {
    heading: 'Recupera más de 10 horas cada semana',
    subheading: 'Con el sistema y la tecnología intuitiva de Lupa - libera más de 12.000 € en tiempo veterinario al año.',
    card1Title: 'Recupera más de 10 horas cada semana',
    card1Sub: 'Con el sistema y la tecnología intuitiva de Lupa - libera más de 12.000 € en tiempo veterinario al año.',
    card2Title: '+20% de valor de vida del cliente',
    card2Sub: 'La reserva automatizada, el seguimiento y la facturación de Lupa generan más visitas, mayor fidelidad y más ingresos.',
    card3Title: 'Reduce el gasto tecnológico un 50%',
    card3Sub: 'Sustituye múltiples herramientas independientes por las funciones nativas de Lupa - ahorrando más de 4.000 € al año.',
  },
  testimonials: {
    heading: 'La confianza de las mejores clínicas veterinarias',
    prevAriaLabel: 'Opinión anterior',
    nextAriaLabel: 'Siguiente opinión',
    slides: [
      {
        quote: '«Puedo leer tres holters por hora, lo que significa que soy el doble de eficiente. Desde el punto de vista del negocio, lo que antes nos llevaba 5 días ahora lo hacemos cómodamente en 3».',
        role: 'Director y Especialista RCVS en Cardiología',
      },
      {
        quote: '«Cuando hemos tenido un problema, vuestro equipo de soporte ha estado ahí para resolverlo, sin importar la hora… eso es lo que os diferencia de cualquier otra transición de PMS que hayamos hecho».',
        role: 'Fundador y Director',
      },
      {
        quote: '«Todos habéis sido extraordinarios. Vuestros técnicos son auténticos genios. Estoy completamente impresionada con todos ellos».',
        role: 'Directora Clínica',
      },
      {
        quote: '«Lupa cambió por completo mi día a día como veterinaria, dándome mucho más tiempo para mis pacientes y menos para el ordenador».',
        role: 'Cirujana Veterinaria',
      },
    ],
  },
  finalCta: {
    heading: 'El futuro del cuidado animal está en nuestras manos',
    sub: 'Lupa está creando el ecosistema más conectado, inteligente y completo del mundo para los 1.000 millones de mascotas.',
    button: 'Sé parte de él.',
  },
  notesFinalCta: {
    heading: 'Impulsa la productividad de tu equipo',
    sub: 'Deja que tu equipo se centre en la atención, no en la administración.',
    button: 'Prueba Lupa Notes',
  },
  migrationsFinalCta: {
    heading: '¿Listo para dar el siguiente paso?',
    sub: 'Entendemos que cada clínica es diferente. Por eso adaptamos tu proceso desde el primer día.',
    button: 'Habla con nosotros',
  },
  pricingFinalCta: {
    heading: 'Invierte en tu negocio hoy',
    button: 'Más información',
  },
  teamFinalCta: {
    heading: 'Construye el futuro con los mejores',
    sub: 'Avanzamos rápido, pero nunca a costa de la solidez. Únete a nosotros para crear tecnología hecha para durar.',
    button: 'Nuestras vacantes',
  },
  footer: {
    tagline: 'SALUD ANIMAL IMPULSADA POR IA',
    email: 'hello@lupapets.com',
    company: 'Empresa',
    legal: 'Legal',
    terms: 'Términos y condiciones',
    privacy: 'Política de privacidad',
    dataComplaint: 'Reclamación de protección de datos',
    followUs: 'Síguenos',
    copyright: '© Lupa Pets 2025 Todos los derechos reservados',
    appStore: 'Descarga en el App Store',
    googlePlay: 'Disponible en Google Play',
    downloadApp: 'Descargar app',
  },
  pricing: {
    heroHeading: 'Invierte en el éxito de tu clínica',
    heroSubtitle: 'Explora nuestros planes de precios y descubre cuánto más podría lograr tu clínica',
    calcHeading: 'Calcula tu presupuesto personalizado',
    calcSub: 'Ajusta los parámetros abajo para ver tu presupuesto personalizado',
    monthlyCostLabel: 'COSTE MENSUAL',
    oneTimeSetupLabel: 'CONFIGURACIÓN INICIAL',
    trainingNote: 'Paquetes opcionales de formación y migración de datos disponibles según tus necesidades.',
    usersTooltip: 'Número de veterinarios y personal que usará Lupa de forma activa.',
    sites: 'Centros',
    users: 'Usuarios',
    sitesLabel: 'Número de centros',
    usersLabel: 'Número de usuarios de Lupa',
    sitePrice: '100 €/mes por centro',
    userPrice: '50 €/usuario/mes',
    notesPrice: '35 €/usuario al mes',
    notesDiscount: '100% de descuento (oferta limitada)',
    total: 'Total mensual',
    totalRecurring: 'Cuota mensual recurrente',
    investHeading: 'Tu inversión',
    investSub: 'Precios transparentes sin comisiones ocultas',
    setupNote: 'CONFIGURACIÓN INICIAL - contáctanos para el precio',
    makeSwitch: 'Haz el cambio',
    bookDemo: 'Reservar demo',
    customPlan: 'Contáctanos para un plan personalizado',
    contactUsBtn: 'Contáctanos',
    moreThanSites: '¿Más de 5 centros? Habla con nuestro equipo para un plan personalizado.',
    moreThanUsers: '¿Más de 30 usuarios? Habla con nuestro equipo para un plan personalizado.',
    noteFree: 'GRATIS (100% de descuento)',
    perMonth: '/mes',
    decreaseSites: 'Reducir centros',
    increaseSites: 'Aumentar centros',
    decreaseUsers: 'Reducir usuarios',
    increaseUsers: 'Aumentar usuarios',
    roiHeading: 'Explora nuevas oportunidades de ingresos con Lupa',
    roiSub: 'Descubre la diferencia que supone Lupa para una clínica de un centro y 3 veterinarios.',
    notesCard: {
      title: 'Lupa Notes',
      revenue: '6.000 €+/mes',
      revenueLabel: 'Ganancia mensual estimada',
      assumptions: 'Supuestos',
      benefits: 'Beneficios',
      assumption1: 'Ahorra 60–90 minutos por jornada de consultas',
      assumption2: 'Los veterinarios consultan 3–4 días/semana',
      assumption3: 'Duración media de consulta 15–30 minutos',
      assumption4: 'Precio medio de consulta 60 €',
      benefit1: 'Ahorra hasta 5 horas por veterinario cada semana',
      benefit2: 'Crea tiempo para 10+ consultas adicionales o 3+ cirugías por veterinario/semana',
    },
    reminderCard: {
      title: 'Sistema de recordatorios',
      revenue: '8.000 €+/mes',
      assumption1: 'Media de 180 citas reservadas por semana actualmente',
      assumption2: '20% de aumento en consultas semanales gracias a la mejor gestión de clientes',
      assumption3: 'Precio medio de cita 60 €',
      benefit1: 'Hasta 36 citas adicionales por semana con recordatorios y CRM',
      benefit2: '144 citas adicionales al mes',
    },
    chargeCard: {
      title: 'Captura de cargos',
      revenue: '2.000 €+/mes',
      assumption1: 'Valor total de facturas al mes: 50.400 € para un centro y 3 veterinarios',
      benefit1: 'Mejora del 5% en la captura de cargos perdidos',
      benefit2: 'Ayuda a recuperar ingresos perdidos con una facturación precisa y asistida',
    },
    testimonialQuote: '"Es un cambio de vida… me ahorra 5–10 minutos en cada consulta, eso 10–20 veces al día"',
    testimonialAttr: 'Paul Irvine-Smith - Cirujano Veterinario y Director Clínico, Witterings Vets',
    testimonialName: 'Paul Irvine-Smith',
    testimonialRole: 'Cirujano Veterinario y Director Clínico, Witterings Vets',
    diagramHeading: 'Reduce el gasto a la mitad, duplica el rendimiento',
    diagramSub: 'Cambia tu colección de herramientas independientes por un ecosistema diseñado para funcionar en sincronía.',
    enterpriseHeading: '¿Empresa o grupo más grande?',
    enterpriseCta: 'Contáctanos',
    enterpriseDesc: 'Ponte en contacto para hablar sobre precios personalizados, funciones a mayor escala y soporte dedicado - desde la incorporación hasta sacar el máximo partido a Lupa.',
    logoCloudHeading: 'Únete a las clínicas que ya se benefician del cambio',
  },
  enterprise: {
    heroHeading1: 'Sistema Operativo',
    heroHeading2: 'Veterinario para Empresas',
    heroSubtitle:
      'La primera solución empresarial del mundo para grupos veterinarios - desde el control centralizado hasta la configuración a nivel de clínica individual',
    heroCta: 'Tu plan personalizado →',
    responseLabel: 'Tiempo de respuesta',
    responseUnit: 'min',
    ratingLabel: 'Puntuación Capterra',
    uptimeLabel: 'Disponibilidad garantizada',
    featHeading: 'Optimizado para el crecimiento, la seguridad y el éxito',
    featSub: 'Dando a tu equipo el apoyo, las herramientas y la protección que necesitan para triunfar',
    support: {
      title: 'Soporte Enterprise',
      b1: 'Equipo de éxito enterprise dedicado y gestor de cuenta.',
      b2: 'Soporte humano + IA disponible 24/7.',
      b3: 'Planes de incorporación personalizados para grandes grupos.',
      b4: 'SLAs de primer nivel con tiempos de respuesta garantizados.',
    },
    growth: {
      title: 'Hecho para crecer',
      b1: 'Crea y configura nuevas sucursales en minutos con ajustes preconfigurados de servicios, precios y flujos de trabajo.',
      b2: 'La migración de datos con IA garantiza una transferencia precisa de los registros existentes en 12 horas.',
      b3: 'Posibilidad de dar identidad de marca a cada clínica.',
      b4: 'Expansión sencilla entre regiones con plantillas y configuraciones centralizadas.',
    },
    security: {
      title: 'Seguridad de nivel bancario',
      b1: 'Certificado SOC 2 Tipo II y cumplimiento del RGPD.',
      b2: 'Pistas de auditoría completas para el cumplimiento empresarial.',
      b3: 'Inicio de sesión único (SSO) y acceso basado en roles.',
      b4: 'Cifrado de nivel bancario (TLS 1.2 en tránsito, AES 256 bits en reposo).',
      link: 'Más sobre seguridad',
    },
    hierarchyHeading: 'Gestiona la complejidad empresarial con facilidad',
    hierarchySub:
      'Establece consistencia a nivel central y decide dónde se aplica la flexibilidad en grupos, clínicas y departamentos.',
    tabs: {
      enterprise: 'Nivel empresa',
      regional: 'Nivel regional',
      clinic: 'Nivel clínica',
      department: 'Nivel departamento',
    },
    enterpriseTab: {
      description:
        'El nivel más alto que supervisa todas las regiones, estableciendo directrices, estándares y controles para todo el grupo.',
      b1: 'Servicios de referencia, listas de inventario y precios estandarizados en todos los centros',
      b2: 'Actualizaciones de toda la empresa distribuidas al instante a cada clínica',
      b3: 'Reglas globales de facturación, descuentos y protocolos definidas centralmente',
      b4: 'Estándares de cumplimiento, seguridad y gobernanza aplicados globalmente',
    },
    regionalTab: {
      description: 'Grandes geografías o sucursales que coordinan múltiples clínicas dentro de la misma área o marca.',
      b1: 'Los gerentes regionales acceden a KPIs financieros, operativos y clínicos de todas las clínicas',
      b2: 'Las clínicas de la misma región pueden compartir datos clínicos y de clientes para derivaciones',
      b3: 'Listas de servicios, niveles de stock y permisos ajustados al instante en todo el grupo',
      b4: 'Herramientas de terceros integradas una vez y usadas en toda la región',
    },
    clinicTab: {
      description: 'El centro veterinario individual donde el equipo puede personalizar los flujos de trabajo dentro de los parámetros establecidos.',
      b1: 'La sede define el marco de permisos para los niveles de autonomía de la clínica',
      b2: 'Las clínicas ajustan listas de stock, servicios, horarios y métodos de comunicación dentro de los límites de la sede',
      b3: 'Datos de clientes e historiales médicos accesibles y sincronizados dentro y opcionalmente entre clínicas',
      b4: 'Configuraciones de idioma, moneda e impuestos aplicadas a centros individuales',
    },
    departmentTab: {
      description:
        'Departamentos dentro de una clínica (p.ej. equipos de derivación, turnos de día/noche) para una mayor personalización de roles o funciones específicas.',
      b1: 'Los departamentos crean sus propios servicios, tipos de cita y plantillas',
      b2: 'Flujos de trabajo, recordatorios y documentación personalizados para equipos específicos',
      b3: 'Permisos y niveles de acceso establecidos de forma diferente por departamento',
      b4: 'Gestión de agenda, planificación y reservas online específicas por equipo activadas',
    },
    readyHeading: 'Gestiona tu grupo como una sola clínica.',
    readySub:
      'Precios personalizados, funciones a mayor escala y un equipo dedicado - desde la incorporación hasta tu clínica número cien.',
    readyCta1: 'Personaliza tu presupuesto →',
    readyCta2: 'Ver todas las funciones →',
    readyBadge: '+ PLAN DE GRUPO',
    readySpecialist: 'Soporte especializado y gestor dedicado',
    readySetup: 'Configuración rápida - clínicas activas en 12 horas',
    readyControl: 'Control centralizado en cada sede',
    readySecurity: 'Seguridad de nivel bancario · SOC 2 · GDPR',
  },
  migrations: {
    heroHeading: 'Pásate a Lupa',
    heroSubtitle: 'Lupa gestiona tu incorporación con rapidez, precisión y apoyo total - haciendo que el cambio sea fácil.',
    heroCta: 'Saber más',
    reliabilityHeading: 'La migración de datos más fiable del mercado',
    reliabilitySub: 'Completa, rápida y ejecutada con máxima precisión - confía en nuestro equipo experto con tus datos.',
    complete: {
      title: 'Migración completa',
      body: 'Damos un paso más desde el primer día. Como el único PMS que transfiere todos los registros financieros y médicos, nos aseguramos de que nada quede atrás durante tu migración.',
    },
    accuracy: {
      title: 'Precisión',
      body: 'Revisado por ingenieros expertos y mejorado con más de 100 puntos de control. Marcamos el estándar del sector en exhaustividad y fiabilidad.',
    },
    experience: {
      title: 'Experiencia',
      body: 'Solo contratamos a los mejores. Nuestros ingenieros de datos tienen años de experiencia en el sector veterinario y entienden tus datos.',
    },
    integrationsHeading: 'Integra las herramientas que usas',
    integrationsSub: 'Integrado con todo lo esencial - desde el primer momento.',
    testimonialsHeading: 'Migraciones que hablan por sí solas',
    prevAriaLabel: 'Testimonio de migración anterior',
    nextAriaLabel: 'Siguiente testimonio de migración',
    migrationQuotes: [
      'Nunca es demasiada molestia, siempre han estado ahí para ayudarnos',
      'Hemos recibido un soporte increíble por vuestra parte… toda la formación previa marcó una diferencia enorme',
      'Sin duda ha sido una de las transiciones más fáciles y fluidas que hemos vivido… y he pasado por varias en mi carrera',
      'En cuanto a integraciones, Lupa ha sido la mejor hasta ahora y el soporte del equipo ha sido increíble',
      'Una de mis grandes preocupaciones era la transferencia de datos… los ingenieros han sido muy serviciales',
    ],
    gdprCompliantLabel: 'CONFORME',
    securityHeading: 'Tus datos, protegidos',
    securityTab1: 'Seguridad y privacidad de datos',
    securityTab2: 'Infraestructura y fiabilidad',
    securityTab3: 'RGPD',
    securityBody1:
      'En Lupa, proteger los datos de tu clínica no es negociable. Cumplimos con SOC 2 y el RGPD, con cifrado de nivel bancario (TLS 1.2 en tránsito, AES 256 bits en reposo). Los controles de administrador y los registros de auditoría detallados garantizan que solo las personas adecuadas accedan a los datos correctos.',
    securityBody2:
      'Tus datos se respaldan cada 2 minutos y se alojan en tu región en una infraestructura AWS segura y escalable - la misma plataforma de confianza que usan las principales organizaciones globales. Priorizamos la fiabilidad en todos los niveles.',
    securityBody3:
      'Lupa cumple con los principales estándares internacionales de privacidad, incluido el RGPD, de modo que nuestros sistemas permanecen en cumplimiento en la mayoría de geografías. Los datos de tu clínica se mantienen seguros, privados y conformes - dondequiera que operes.',
    securityBtn: 'Nuestras medidas de seguridad',
    onboardingHeading: 'Incorporación a Lupa, paso a paso',
    onboardingSub: 'Nuestro equipo especialista te acompaña paso a paso, a tu ritmo.',
    steps: {
      planning: {
        title: 'Planificación',
        duration: '1 día',
        body: 'Conoce a tu asesor de incorporación dedicado. Aprendemos los flujos de trabajo, valores y dinámica de tu equipo para adaptar Lupa a tu medida.',
      },
      config: {
        title: 'Configuración',
        duration: '4-8 semanas',
        body: 'Limpiaremos tus datos, configuraremos tu sistema y guiaremos las integraciones con tus socios habituales para adaptar Lupa a tu forma de trabajar.',
      },
      training: {
        title: 'Formación',
        duration: '2-4 semanas',
        body: 'Tu equipo recibirá una formación completa en nuestro entorno sandbox, con sesiones guiadas y ejercicios a tu propio ritmo. Tu éxito es nuestra responsabilidad.',
      },
      golive: {
        title: 'Soporte en el lanzamiento',
        duration: '2 días (clínicas en migración)',
        body: 'Tu asesor estará presente en persona el día del lanzamiento para apoyarte a ti y a tu equipo, garantizando un inicio tranquilo con el mínimo estrés.',
      },
      hypercare: {
        title: 'Hipersoporte',
        duration: '3 semanas',
        body: 'La mayor parte del aprendizaje ocurre en la práctica. Durante tres semanas tras el lanzamiento, te proporcionamos apoyo directo para que tu equipo gane confianza.',
      },
      ongoing: {
        title: 'Soporte continuo',
        duration: 'Indefinidamente',
        body: 'Nuestro equipo de Éxito de Cliente hace seguimiento proactivo, sigue tus objetivos y garantiza que Lupa evolucione con tus necesidades - con un tiempo de respuesta de 2 min 35 s y una satisfacción del 98,2%.',
      },
    },
    faqHeading: 'Tus preguntas respondidas',
    faqSub: '¿Necesitas más información?\nEnvíanos un mensaje y te responderemos en breve.',
    faq: [
      {
        question: '¿Habrá alguien conmigo el día del lanzamiento?',
        answer:
          'Para clínicas en migración, sí - tu asesor de incorporación dedicado estará en persona el día del lanzamiento para apoyarte a ti y a tu equipo. Para prácticas nuevas, el soporte presencial en el lanzamiento no está incluido por defecto, pero puede contratarse como servicio adicional.',
      },
      {
        question: '¿Con quién se integra Lupa?',
        answer:
          'Lupa se integra con todos los socios clave en los que se apoya una clínica veterinaria. Para diagnóstico de laboratorio, conectamos con IDEXX y Zoetis. Para pedidos de farmacia, integramos NVS y MWI. Para reclamaciones de seguros, trabajamos con Vetenvoy. También soportamos integración PACS para imagen, además de Stripe, Dojo, Outlook, Gmail y más.',
      },
      {
        question: '¿Qué es una API abierta y qué significa para mi clínica?',
        answer:
          'Una API abierta es la forma en que diferentes sistemas de software se comunican entre sí. En la práctica, significa que Lupa puede conectarse con herramientas y servicios más allá de nuestras integraciones existentes - ya sea una plataforma de derivaciones, un paquete de contabilidad o algo específico de tu clínica.',
      },
      {
        question: '¿Cuánto tiempo tarda el cambio a Lupa?',
        answer:
          'El proceso de incorporación completo suele tardar entre 6 y 12 semanas de principio a fin. Incluye una sesión de planificación, 4-8 semanas de migración de datos y configuración del sistema, 2-4 semanas de formación del equipo y, a continuación, el lanzamiento con soporte de hipersoporte durante 3 semanas.',
      },
      {
        question: '¿Perderé datos al cambiar desde mi PMS actual?',
        answer:
          'No. Lupa es el único PMS que transfiere todos los registros financieros y médicos durante la migración. Nuestros ingenieros de datos especializados revisan cada migración con más de 100 puntos de control de calidad para garantizar que nada se pierda o corrompa.',
      },
      {
        question: '¿Qué soporte recibo después del lanzamiento?',
        answer:
          'No te dejamos solo. Tras el lanzamiento, dispones de 3 semanas de hipersoporte - apoyo directo mientras tu equipo gana confianza con el sistema. Después, nuestro equipo de Éxito de Cliente hace seguimiento proactivo con un tiempo de respuesta medio de menos de 3 minutos y una satisfacción del 98,2%.',
      },
      {
        question: '¿Puedo cambiar a Lupa desde cualquier sistema de gestión?',
        answer:
          'Sí. El equipo de migración de Lupa tiene experiencia transfiriendo datos desde una amplia gama de plataformas veterinarias PMS. Nuestros ingenieros de datos se especializan en datos veterinarios y comprenden los matices de los distintos sistemas.',
      },
    ],
  },
  notes: {
    heroHeading1: 'Transcribe y resume historiales',
    heroHeading2: 'sin esfuerzo con Lupa Notes',
    heroSubPre: 'Tu inversión en IA que genera ',
    heroSubHighlight: '27.000 € adicionales por veterinario al año',
    heroSubPost: ' y aumenta notablemente la satisfacción y productividad del personal.',
    heroCta: 'Contáctanos',
    stepsHeading: 'Lupa Notes es el futuro de la atención veterinaria',
    stepsSub: 'Nuestro objetivo es transformar permanentemente el sector veterinario dotando a los veterinarios más ocupados de herramientas fáciles de usar.',
    steps: [
      {
        label: 'Grabar',
        description:
          'Captura cada momento de la consulta - de principio a fin en tu teléfono o computadora. Mantente completamente enfocado en tu paciente y cliente mientras la consulta se transcribe en segundo plano.',
      },
      {
        label: 'Resumir',
        description:
          'Filtramos la conversación intrascendente y convertimos cada consulta en notas médicas claras y concisas. Elige una plantilla o crea la tuya - Lupa la formateará al instante.',
      },
      {
        label: 'Lista de acciones',
        description:
          'No pierdas nunca la pista de lo que hay que hacer. Crea automáticamente listas de tareas - desde llamadas de seguimiento hasta coordinar una derivación. Mantente organizado y cumple cada promesa.',
      },
      {
        label: 'Enriquecer',
        description:
          '¿Necesitas incluir más? Añade notas de voz, sube cartas de derivación, historial médico o resultados de laboratorio con tu grabación. Lupa incorpora los documentos médicos clave en tus resúmenes para hacerlos completos.',
      },
      {
        label: 'Exportar',
        description:
          'Revisa tus notas manualmente o con la ayuda de nuestro agente de IA, Jerry, antes de subirlas a tu PMS. Crea resúmenes para el cliente para que se los lleven a casa, todo con un clic.',
      },
    ],
    benefitsHeading1: 'Un cambio sencillo,',
    benefitsHeading2: 'infinitos beneficios',
    benefitsSub: 'Miles de veterinarios ahorran 60 minutos al día con Lupa Notes. Tú también puedes.',
    benefitsCta: 'Reservar prueba gratis',
    benefit1Title: 'Ahorra 60 minutos por veterinario al día',
    benefit1Desc: 'Libera tiempo para operaciones, informes y atención al paciente automatizando tareas administrativas manuales.',
    benefit2Title: 'Mejor experiencia y fidelización del cliente',
    benefit2Desc:
      'Dedica menos tiempo a escribir y más tiempo a relacionarte con los clientes. Crea resúmenes de consulta para mantenerles informados y alineados con el plan.',
    benefit3Title: 'Captura más ingresos, sin esfuerzo',
    benefit3Desc:
      'Aumenta los ingresos liberando tiempo para ofrecer servicios adicionales como corte de uñas y análisis de sangre. La IA de Lupa señalará los cargos no facturados directamente desde tus notas.',
    worksWith: 'Usa Lupa Notes a tu manera',
    worksWithOption1Title: 'Incluido en el plan',
    worksWithOption1Desc: 'Lupa Notes ya está integrado en tu suscripción a Lupa OS.',
    worksWithOption2Title: 'Funciona con cualquier PMS',
    worksWithOption2Desc:
      '¿Aún no usas Lupa OS? Sin problema. Lupa Notes funciona perfectamente con cualquier PMS - empieza a ahorrar tiempo de inmediato.',
    testimonialsHeading1: 'La confianza de clínicas',
    testimonialsHeading2: 'de todo el sector',
    prevAriaLabel: 'Testimonio anterior',
    nextAriaLabel: 'Siguiente testimonio',
    notesTestimonials: [
      {
        designation: 'Cirujano Veterinario, Woodstock',
        quote: '«Creo que me ahorra aproximadamente una hora al día en una jornada normal. En un día más ajetreado, me ahorraría aún más… los clientes no esperan, lo que mejora la puntuación NPS, algo realmente importante ahora mismo».',
      },
      {
        designation: 'Director, Witterings Vets',
        quote: '«Ha cambiado mi vida… disfruto otra vez siendo veterinario, volviendo a cuidar a mis pacientes y dejando de escribir notas clínicas».',
      },
      {
        designation: 'Propietaria, Burghley Veterinary Centre',
        quote: '«Vuestra herramienta de transcripción con IA es un auténtico cambio de juego. ¡No me puedo creer la rapidez con la que se está transformando y mejorando!».',
      },
      {
        designation: 'Cirujana Veterinaria, Derbyshire',
        quote: '«Sigo concentrada y atenta a lo que me cuentan, pero me da la tranquilidad de saber que aunque no recuerde todo o no lo procese del todo, va a quedar reflejado en la transcripción y en las notas».',
      },
      {
        designation: 'Cirujano Veterinario, Coventry',
        quote: '«Es una decisión obvia. La verdad, casi da vergüenza la cantidad de tiempo que estoy ahorrando al día… vuelvo a hacer consultas en 10 minutos en vez de 15».',
      },
      {
        designation: 'Director Veterinario, Pet People',
        quote: '«Mi experiencia personal es que esto es increíble. Disfruto mucho más de las consultas. No estoy pensando en recordar cosas para apuntarlas, ni interrumpiendo la conversación para anotar información importante».',
      },
    ],
    faqHeading: 'Tus preguntas respondidas',
    faq: [
      {
        question: '¿Están seguros mis datos en Lupa Notes?',
        answer:
          'Sí. Lupa Notes está construido con la seguridad y privacidad de datos como pilares. Cumplimos con el RGPD, estamos certificados SOC 2 Tipo II y utilizamos TLS 1.2 en tránsito y cifrado AES 256 bits en reposo. Tus grabaciones y notas se almacenan de forma segura y nunca se comparten con terceros.',
      },
      {
        question: '¿Debo obtener el consentimiento del cliente antes de grabar una consulta?',
        answer:
          'Sí, siempre debes pedir consentimiento a tu cliente antes de iniciar la grabación. Lo hacemos sencillo con flujos de consentimiento listos para usar y plantillas de señalética para la clínica. La mayoría de los clientes aceptan una vez que entienden el beneficio.',
      },
      {
        question: '¿Lupa Notes guarda el audio de mis consultas?',
        answer:
          'No, Lupa Notes no almacena permanentemente tus grabaciones de audio. El audio se retiene temporalmente para su procesamiento y verificación de precisión, y luego se elimina. Solo se conservan las notas de texto estructuradas y los resúmenes en tus registros.',
      },
      {
        question: '¿Cuánto tiempo ahorra realmente Lupa Notes a los veterinarios?',
        answer:
          'Los veterinarios que usan Lupa Notes ahorran una media de 60 minutos al día en toma de notas clínicas y administración. A lo largo de un año, para una clínica con 3 veterinarios, eso se traduce en un tiempo significativo recuperado - tiempo que puede reinvertirse en consultas, cirugías o simplemente salir a tiempo.',
      },
      {
        question: '¿Son precisas las notas generadas por IA?',
        answer:
          'Sí - pero siempre tienes el control. Lupa Notes captura la transcripción completa de la consulta y genera notas estructuradas basadas en tu plantilla. Antes de que se guarde nada en el historial del paciente, tú revisas y apruebas las notas.',
      },
      {
        question: '¿Puedo personalizar el formato de las notas que genera Lupa Notes?',
        answer:
          'Sí. Lupa Notes te permite elegir entre plantillas de notas existentes o crear tu propio formato personalizado. Puedes tener distintas plantillas por especie, tipo de consulta o equipo clínico - dándote control total sobre cómo se estructuran tus registros.',
      },
    ],
  },
  team: {
    heroHeading1: 'Ayúdanos a construir',
    heroHeading2: 'la plataforma mundial de salud animal',
    heroSubtitle: 'Únete a Lupa y deja tu huella en la atención veterinaria mundial',
    heroCta: 'Únete a nuestro equipo',
    europeHeading: 'El equipo de startups nº1 de Europa',
    europeSub: 'Nuestro equipo proviene de las mejores organizaciones tecnológicas y de estrategia del mundo.',
    valuesHeading: 'Los valores que nos definen',
    valuesSub: 'Los cimientos de nuestra empresa y cultura.',
    values: [
      { title: 'Confía en tu instinto', desc: 'Confiamos en que lideres con convicción, actúes con intuición y experiencia, y seas dueño de tus decisiones.' },
      { title: 'Enamórate cada día', desc: 'Te animamos a dar forma a tu rol para que encuentres sentido y satisfacción en tu trabajo cada día.' },
      { title: 'Trátalo como tuyo', desc: 'Creemos en la verdadera apropiación - responsabilizándote, entregando con orgullo y sabiendo que tu esfuerzo es reconocido y forma parte de nuestro éxito colectivo.' },
      { title: 'Actúa con máxima urgencia', desc: 'Nos movemos rápido y con cuidado, con propósito. Cada decisión y acción genera un impacto real en esta empresa.' },
      { title: 'Sé audaz y construye el futuro', desc: 'En Lupa fomentamos el pensamiento audaz y las ideas frescas - empujamos límites para construir soluciones.' },
    ],
    gridHeading: 'El equipo detrás de Lupa',
    gridSub: 'Conoce algunas de las personas que dan forma al producto.',
    formerly: 'Anteriormente',
    headcountHeading: 'Equipo Pequeño, Gran Impacto',
    headcountSub: 'Únete a nuestro equipo cuidadosamente seleccionado, con la confianza y el respaldo para construir algo con impacto.',
    eliteEngineers: 'Ingenieros de élite',
    raised: 'Recaudado hasta la fecha',
    petsRegistered: 'Mascotas registradas',
    softwareTab: 'Ingenieros de software',
    dataTab: 'Ingenieros de datos',
    productTab: 'Equipo de producto',
    clientTab: 'Equipo de clientes',
    showLess: 'Ver menos',
    showAll: 'Ver todos',
    principlesEyebrow: 'Ingeniería',
    principlesHeading1: 'Principios que impulsan nuestro',
    principlesHeading2: 'equipo de ingeniería',
    principlesSub: 'Hemos construido nuestra cultura de ingeniería sobre las lecciones más importantes que nuestro equipo aprendió en las mejores empresas tecnológicas del mundo.',
    principles: [
      {
        principle: 'Construir para escalar',
        quote: 'Construimos sistemas duraderos. Valoramos las bases sólidas, las pruebas con sentido y las decisiones técnicas inteligentes que nos mantienen rápidos hoy y preparados para el mañana.',
      },
      {
        principle: 'Entregar rápido',
        quote: 'Nos movemos rápido para dar vida a las ideas. Los ingenieros construyen, despliegan y ven el impacto inmediato de su trabajo.',
      },
      {
        principle: 'Autonomía',
        quote: 'La responsabilidad de principio a fin es clave en cómo trabajamos. En Lupa, resolver problemas reales empieza por la autonomía y se refuerza con la colaboración.',
      },
      {
        principle: 'Obsesión por el cliente',
        quote: 'Mantenemos contacto cercano con las clínicas - su feedback guía lo que construimos y cómo lo refinamos.',
      },
      {
        principle: 'Contratar a los mejores',
        quote: 'Creemos en construir con talento de élite. Subimos el listón a propósito, porque trabajar con los mejores es la forma de crecer más rápido y construir mejor.',
      },
    ],
    techHeading: 'Nuestro stack tecnológico de vanguardia',
    techFrontend: 'Frontend',
    techBackend: 'Backend',
    techDatabase: 'Base de datos',
    techDataAnalytics: 'Datos y analítica',
    techMobile: 'App móvil',
    benefitsHeading: 'Beneficios que obtienes en Lupa',
    benefitsSub: 'Invertimos en nuestro equipo para que ellos puedan invertir en Lupa (literalmente).',
    benefits: [
      { title: 'Compensación excepcional', desc: 'Paquete salarial top de mercado, diseñado para atraer y retener al mejor talento.' },
      { title: 'Equity para todos', desc: 'Todo el equipo recibe equity, sin excepción: comparte el éxito a medida que crecemos.' },
      { title: '25 días de vacaciones', desc: '25 días anuales para descansar, recargar y explorar el mundo.' },
      { title: 'Visado / reubicación', desc: 'Visado patrocinado y paquete completo de reubicación para instalarte en Londres.' },
      { title: 'Seguro médico privado', desc: 'Seguro médico privado top tier para ti y tu tranquilidad.' },
      { title: 'MacBook Air M4', desc: 'El último MacBook Air M4 más las herramientas de productividad e IA que necesites.' },
      { title: 'Viaje anual de equipo', desc: 'Viaje anual de equipo al extranjero y socials cada dos semanas: trabajamos y disfrutamos juntos.' },
      { title: 'Oficina en el centro de Londres', desc: 'Oficina premium en el centro de Londres, diseñada para concentración y colaboración.' },
      { title: 'Café y cerveza gratis', desc: 'Café de barista y cerveza de barra gratis para mantener la energía.' },
      { title: 'Gimnasio subvencionado', desc: 'Membresía de gimnasio subvencionada para que mantenerte en forma encaje en tu día.' },
    ],
  },
  contact: {
    tabDemo: '📋 Reservar demo',
    tabMessage: '✏️ Enviar mensaje',
    demoHeading: '🚀 Ve Lupa en acción',
    demoSub: 'Reserva una demo para ver cómo Lupa puede transformar tu clínica.',
    demoQuote: 'Cada persona que hemos conocido en Lupa ha sido increíble - atenta, servicial e impresionantemente inteligente.',
    demoQuoteAttr: '- Natasha, Polden Hills Veterinary Centre',
    msgHeading: '🚀 Estamos aquí para ayudarte',
    msgSub: '¿Tienes preguntas? Envíanos un mensaje y te responderemos en 4 horas laborables.',
    msgQuote: 'Lupa ha sido lo mejor hasta ahora, ¡y el apoyo de vosotros y el equipo ha sido increíble!',
    msgQuoteAttr: '- Veterinario, Norwich',
    trustedBy: 'La confianza de clínicas en todo el mundo',
    namePlaceholder: 'Tu nombre completo',
    nameLabel: 'Nombre',
    emailLabel: 'Email',
    emailPlaceholder: 'tu@tuclinica.com',
    companyLabel: 'Nombre de empresa',
    companyPlaceholder: 'Tu clínica o práctica',
    locationLabel: '¿Dónde estás ubicado?',
    locationSelect: 'Selecciona...',
    submitBtn: 'Enviar',
    successTitle: '¡Solicitud recibida!',
    successSub: 'Nuestro equipo se pondrá en contacto en 4 horas laborables.',
    fullName: 'Nombre completo*',
    emailAddr: 'Dirección de email*',
    phone: 'Número de teléfono',
    company: 'Empresa',
    message: 'Mensaje',
    privacyLabel: 'He leído y acepto la ',
    privacyLink: 'Política de privacidad*',
    termsLabel: ' y los ',
    termsLink: 'Términos y condiciones*',
    sendBtn: 'Enviar mensaje',
    msgSuccessTitle: '¡Mensaje enviado!',
    msgSuccessSub: 'Te responderemos en 4 horas laborables.',
    requiredField: 'Este campo es obligatorio',
    privacyRequired: 'Acepta la Política de privacidad y los Términos',
  },
  blog: {
    heroHeading: 'El blog de Lupa',
    heroSub: 'Información sobre gestión de clínicas veterinarias, IA y crecimiento de tu práctica',
    latest: 'Todas',
    allCategories: 'Todas',
    readMore: 'Leer más',
    techAI: 'Tech e IA',
    lupaNews: 'Noticias de Lupa',
    businessInsights: 'Perspectivas de negocio',
    videos: 'Vídeos',
    backToBlog: 'Volver al blog',
    noPostsFound: 'No se encontraron artículos.',
    jumpToSection: 'Ir a la sección',
    shareArticle: 'Compartir artículo',
    joinMailing: 'Únete a nuestra lista de correo',
    mailingDesc: 'Mantente al día con las noticias y ofertas de Lupa.',
    emailPlaceholder: 'Tu dirección de email',
    subscribe: 'Suscribirme',
    seeLupa: 'Ve Lupa en acción',
    book30min: 'Reserva una demo de 30 min con nuestro equipo.',
    bookDemo: 'Reservar demo',
    latestBlogs: 'Últimas entradas',
    backToAll: 'Volver a todos los artículos',
    inlineCta: '¿Listo para ahorrar 60+ min por veterinario al día?',
    inlineSub1: 'Lupa ayuda a ',
    inlineSub2: 'tu clínica',
    inlineSub3: ' a evolucionar en ',
    inlineSub4: 'todos los frentes',
    letsTalk: 'Hablemos',
    hubHeading: 'Blogs e historias de éxito',
    hubLede: 'Ideas prácticas para propietarios y gerentes de clínicas veterinarias.',
    featuredLabel: 'Artículo destacado',
    allArticlesLabel: 'Todos los artículos',
    readArticle: 'Leer artículo',
    previous: 'Anterior',
    next: 'Siguiente',
    pageLabel: 'Página',
    paginationLabel: 'Paginación',
    keyPoints: 'Puntos clave',
    breadcrumbBlog: 'Blog',
    clinicalReview: 'Revisión clínica',
    clinicallyReviewed: 'Revisado clínicamente',
    faqHeading: 'Preguntas frecuentes',
    writtenBy: 'Escrito por',
    readMoreAbout: 'Más sobre',
    keepReading: 'Seguir leyendo',
    aboutAuthor: 'Sobre %s',
    articlesBy: 'Artículos de %s',
  },
  cma: {
    heading: 'Protege tu negocio y equipo ante las regulaciones de la CMA',
    sub: '¿Te perdiste nuestro webinar? Ve la grabación - gratis hasta el 30 de noviembre',
    watchBtn: 'Verlo aquí',
  },
  meta: {
    home: {
      title: 'Lupa Pets - Software de Gestión Veterinaria con IA',
      description: 'El primer sistema operativo veterinario con IA listo para empresas del mundo.',
    },
    pricing: {
      title: 'Precios - Lupa Pets',
      description:
        'Precios transparentes y flexibles para clínicas veterinarias de cualquier tamaño. Crea tu plan personalizado y calcula tu ROI.',
    },
    lupaNotes: {
      title: 'Lupa Notes - Escriba veterinario con IA',
      description:
        'Ahorra 60 minutos por veterinario al día con Lupa Notes, el escriba con IA que transcribe y resume tus consultas automáticamente.',
    },
    migrations: {
      title: 'Cambia a Lupa - Migración fluida de tu PMS veterinario | Lupa Pets',
      description:
        'Migración experta del software de gestión veterinaria con transferencia completa de datos, soporte dedicado e integración fluida. Conforme con SOC 2 y un 98,2 % de satisfacción.',
    },
    contact: {
      title: 'Contacto - Lupa Pets',
      description: 'Ponte en contacto con el equipo de Lupa.',
    },
    team: {
      title: 'Equipo - Lupa Pets',
      description:
        'Conoce al equipo que está construyendo la primera plataforma veterinaria de IA lista para empresas. Ingenieros de Google, Meta, Palantir, Amazon y BCG.',
    },
    enterprise: {
      title: 'Enterprise - Lupa Pets',
      description: 'El primer sistema operativo veterinario empresarial del mundo.',
    },
    aaha: {
      title: 'AAHA Advantage - Lupa Pets',
      description:
        'El PIMS diseñado para clínicas acreditadas por AAHA. Precios preferentes, flujos con IA y excelencia clínica - exclusivo para miembros AAHA.',
    },
    blog: {
      title: 'Blog - Lupa Pets',
      description:
        'Ideas sobre gestión de clínicas veterinarias, tecnología de IA, regulaciones CMA y crecimiento de tu clínica.',
    },
    privacyPolicy: {
      title: 'Política de privacidad - Lupa Pets',
      description: 'Política de privacidad de Lupa Pets.',
    },
    dataProtectionComplaints: {
      title: 'Procedimiento de reclamaciones de protección de datos - Lupa Pets',
      description:
        'Cómo presentar una reclamación de protección de datos a Lupa Pets y cómo recibimos, evaluamos, investigamos y respondemos a las reclamaciones conforme a la normativa de protección de datos del Reino Unido.',
    },
    terms: {
      title: 'Términos y condiciones - Lupa Pets',
      description: 'Términos y condiciones de Lupa Pets.',
    },
  },
}

export const de: Translations = {
  nav: {
    scribe: 'KI-Notizen',
    enterprise: 'Enterprise',
    switching: 'Wechseln',
    pricing: 'Preise',
    aboutUs: 'Über uns',
    team: 'Team',
    blog: 'Blog',
    logIn: 'Anmelden',
    lupaOS: 'Lupa OS',
    lupaNotes: 'Lupa Notes',
    contactUs: 'Kontakt',
    closeMenu: 'Menü schließen',
    openMenu: 'Menü öffnen',
    loginMobile: 'Anmelden → Lupa OS',
  },
  announcement: {
    label: 'Ankündigung',
    text: 'Neues Webinar: Erfahren Sie mehr über die kommenden Hospitalisierungsfunktionen in Lupa.',
    cta: 'Jetzt anmelden.',
    dismiss: 'Ankündigung schließen',
  },
  hero: {
    aiPowered: 'KI-gestützt',
    heading: 'Praxisverwaltungssoftware für Tierärzte',
    subtitle: 'Das weltweit erste enterprise-taugliche KI-Betriebssystem für Tierärzte',
    bookDemo: 'Demo buchen',
    contactUs: 'Kontakt aufnehmen',
  },
  integrations: {
    heading: 'Nahtlose Integration mit branchenüblicher Software',
    subheading: 'Integriert mit allem Wesentlichen',
  },
  coreFeatures: {
    heading1: 'Kernfunktionen,',
    heading2: 'vollständig integriert.',
    subheading: 'Alles, was Ihre Klinik für den Erfolg braucht – nativ integriert und intelligent gestaltet.',
    pms: {
      eyebrow: 'PMS neu gedacht',
      title: 'Ihr PMS – durchdacht modernisiert',
      description:
        'Erleben Sie ein Betriebssystem mit allen Kernfunktionen – Konsultationen, Terminplanung, Patientenakten, Abrechnung – von Tierärzten durchdacht und von erstklassigen Ingenieuren entwickelt.',
    },
    comms: {
      eyebrow: 'Kundenkommunikation',
      title: 'Mühelos in Verbindung bleiben',
      description:
        'Erinnerungen, E-Mail-Kampagnen, Online-Buchung, App-Chat. Halten Sie Ihre Kunden engagiert, verbunden und treu.',
    },
    ai: {
      eyebrow: 'KI-Notizerfassung',
      title: 'KI-Transkription – 60 Minuten täglich pro Tierarzt sparen',
      description:
        'Transkribieren und formatieren Sie Ihre medizinischen Notizen sofort – von Konsultationen bis zu OP-Berichten und Anrufen. Mehr Genauigkeit, weniger Zeitaufwand, mehr Zeit für Tier und Halter.',
    },
    finance: {
      eyebrow: 'Finanzen & Analytik',
      title: 'Die finanzielle Gesundheit Ihrer Klinik verstehen',
      description:
        'Steigern Sie aktiv Ihre Einnahmen, indem Sie die Leistung verfolgen und Wachstumschancen erkennen. Alle Daten, die Sie benötigen, immer griffbereit.',
    },
    pullQuote: '„Lupa schließt die Lücke, die jedes andere PMS hinterlässt"',
    pullQuoteAuthor: 'Cameron, Gründer & Tierarzt',
    pullQuoteClinic: 'Essex Vets',
  },
  stats: {
    heading1: 'Unübertroffener Support.',
    heading2: 'Einzigartige Betreuung.',
    petsLabel: 'Registrierte Haustiere',
    featuresLabel: 'Neue Funktionen veröffentlicht in 2025',
    responseLabel: 'Durchschnittliche Reaktionszeit',
    minUnit: 'Min',
    secondUnit: 's',
  },
  logoCloud: {
    heading: 'Schließen Sie sich den Kliniken an, die mit Lupa versteckte Einnahmen erschließen',
  },
  roi: {
    heading: 'Gewinnen Sie jede Woche über 10 Stunden zurück',
    subheading: 'Mit Lupas intuitivem System und Technologie – sparen Sie mehr als 12.000 € Tierarztzeit pro Jahr.',
    card1Title: 'Gewinnen Sie jede Woche über 10 Stunden zurück',
    card1Sub: 'Mit Lupas intuitivem System und Technologie – sparen Sie mehr als 12.000 € Tierarztzeit pro Jahr.',
    card2Title: '+20 % Kundenwert (CLV)',
    card2Sub: 'Lupas automatische Buchung, Nachverfolgung und Abrechnung führen zu mehr Besuchen, stärkerer Kundenbindung und höherem Umsatz.',
    card3Title: 'Technologieausgaben um 50 % senken',
    card3Sub: 'Ersetzen Sie mehrere Einzeltools durch Lupas native Funktionen – und sparen Sie über 4.000 € pro Jahr.',
  },
  testimonials: {
    heading: 'Vertrauen führender Tierkliniken',
    prevAriaLabel: 'Vorherige Bewertung',
    nextAriaLabel: 'Nächste Bewertung',
    slides: [
      {
        quote: '„Ich kann drei Holter pro Stunde auswerten, das macht mich doppelt so effizient. Aus geschäftlicher Sicht schaffen wir damit in 3 Tagen, wofür wir früher 5 Tage gebraucht haben."',
        role: 'Direktor & RCVS-Spezialist für Kardiologie',
      },
      {
        quote: '„Wenn wir ein Problem hatten, war euer Support immer zur Stelle, egal um welche Tageszeit… das hat euch von jedem anderen PMS-Wechsel abgehoben, den ich erlebt habe."',
        role: 'Gründer & Direktor',
      },
      {
        quote: '„Ihr wart alle herausragend. Eure Techniker sind absolute Genies. Ich bin schlicht beeindruckt von ihnen allen."',
        role: 'Klinische Leiterin',
      },
      {
        quote: '„Lupa hat meinen tierärztlichen Alltag komplett verändert – ich habe deutlich mehr Zeit für meine Patienten als für den Computer!"',
        role: 'Tierärztin',
      },
    ],
  },
  finalCta: {
    heading: 'Die Zukunft der Tierpflege gestalten wir',
    sub: 'Lupa baut das vernetzteste, intelligenteste und umfassendste Ökosystem der Welt für die 1 Mrd. Haustiere weltweit.',
    button: 'Seien Sie dabei.',
  },
  notesFinalCta: {
    heading: 'Steigern Sie die Produktivität Ihres Teams',
    sub: 'Lassen Sie Ihre Tierärzte sich auf die Pflege konzentrieren, nicht auf Verwaltung.',
    button: 'Lupa Notes ausprobieren',
  },
  migrationsFinalCta: {
    heading: 'Bereit für den nächsten Schritt?',
    sub: 'Wir wissen, dass jede Klinik anders ist. Deshalb gestalten wir Ihren Weg ab Tag 1 individuell.',
    button: 'Sprechen Sie mit uns',
  },
  pricingFinalCta: {
    heading: 'Investieren Sie heute in Ihr Geschäft',
    button: 'Mehr erfahren',
  },
  teamFinalCta: {
    heading: 'Bauen Sie die Zukunft mit den Besten',
    sub: 'Wir liefern schnell - aber nie auf Kosten der Stabilität. Helfen Sie uns, Technologie zu schaffen, die Bestand hat.',
    button: 'Unsere Stellenangebote',
  },
  footer: {
    tagline: 'KI-GESTÜTZTE TIERGESUNDHEIT',
    email: 'hello@lupapets.com',
    company: 'Unternehmen',
    legal: 'Rechtliches',
    terms: 'AGB',
    privacy: 'Datenschutzrichtlinie',
    dataComplaint: 'Datenschutzbeschwerde',
    followUs: 'Folgen Sie uns',
    copyright: '© Lupa Pets 2025 Alle Rechte vorbehalten',
    appStore: 'Im App Store laden',
    googlePlay: 'Bei Google Play laden',
    downloadApp: 'App laden',
  },
  pricing: {
    heroHeading: 'Investieren Sie in den Erfolg Ihrer Klinik',
    heroSubtitle: 'Erkunden Sie unsere Preispläne und erfahren Sie, wie viel mehr Ihre Klinik erreichen könnte',
    calcHeading: 'Berechnen Sie Ihr individuelles Angebot',
    calcSub: 'Passen Sie die Einstellungen unten an, um Ihr individuelles Angebot zu sehen',
    monthlyCostLabel: 'MONATLICHE KOSTEN',
    oneTimeSetupLabel: 'EINMALIGE EINRICHTUNG',
    trainingNote: 'Optionale Schulungen und Datenmigrationspakete verfügbar je nach Ihren Anforderungen.',
    usersTooltip: 'Anzahl der Tierärzte und Mitarbeiter, die Lupa aktiv nutzen werden.',
    sites: 'Standorte',
    users: 'Nutzer',
    sitesLabel: 'Anzahl der Standorte',
    usersLabel: 'Anzahl der Lupa-Nutzer',
    sitePrice: '100 €/Monat pro Standort',
    userPrice: '50 €/Nutzer/Monat',
    notesPrice: '35 €/Nutzer pro Monat',
    notesDiscount: '100 % Rabatt (begrenztes Angebot)',
    total: 'Gesamt monatlich',
    totalRecurring: 'Monatliche wiederkehrende Gebühr',
    investHeading: 'Ihre Investition',
    investSub: 'Transparente Preise ohne versteckte Kosten',
    setupNote: 'EINMALIGE EINRICHTUNG - Kontaktieren Sie uns für Preisinfos',
    makeSwitch: 'Jetzt wechseln',
    bookDemo: 'Demo buchen',
    customPlan: 'Kontaktieren Sie uns für einen individuellen Plan',
    contactUsBtn: 'Kontakt',
    moreThanSites: 'Mehr als 5 Standorte? Sprechen Sie mit unserem Team für einen individuellen Plan.',
    moreThanUsers: 'Mehr als 30 Nutzer? Sprechen Sie mit unserem Team für einen individuellen Plan.',
    noteFree: 'KOSTENLOS (100 % Rabatt)',
    perMonth: '/Mo',
    decreaseSites: 'Standorte verringern',
    increaseSites: 'Standorte erhöhen',
    decreaseUsers: 'Nutzer verringern',
    increaseUsers: 'Nutzer erhöhen',
    roiHeading: 'Neue Umsatzchancen mit Lupa entdecken',
    roiSub: 'Erleben Sie den Unterschied, den Lupa für eine Klinik mit einem Standort und 3 Tierärzten macht.',
    notesCard: {
      title: 'Lupa Notes',
      revenue: '6.000 €+/Mo',
      revenueLabel: 'Prognostizierter Umsatzgewinn pro Monat',
      assumptions: 'Annahmen',
      benefits: 'Vorteile',
      assumption1: 'Spart 60–90 Minuten pro Beratungstag',
      assumption2: 'Tierärzte beraten 3–4 Tage/Woche',
      assumption3: 'Durchschn. Beratungsdauer 15–30 Minuten',
      assumption4: 'Durchschn. Beratungspreis 60 €',
      benefit1: 'Spart bis zu 5 Stunden pro Tierarzt pro Woche',
      benefit2: 'Schafft Zeit für 10+ zusätzliche Beratungen oder 3+ OPs pro Tierarzt/Woche',
    },
    reminderCard: {
      title: 'Erinnerungssystem',
      revenue: '8.000 €+/Mo',
      assumption1: 'Derzeit durchschnittlich 180 Termine pro Woche',
      assumption2: '20 % mehr wöchentliche Beratungen durch besseres Kundenbindungsmanagement',
      assumption3: 'Durchschn. Terminpreis 60 €',
      benefit1: 'Bis zu 36 zusätzliche Termine pro Woche durch Erinnerungen und CRM',
      benefit2: '144 zusätzliche Termine pro Monat',
    },
    chargeCard: {
      title: 'Leistungserfassung',
      revenue: '2.000 €+/Mo',
      assumption1: 'Gesamtrechnungswert pro Monat: 50.400 € (1 Standort, 3 Tierärzte)',
      benefit1: '5 % Verbesserung bei der Erfassung verpasster Leistungen',
      benefit2: 'Hilft, entgangene Einnahmen durch genaue und unterstützte Abrechnung zurückzugewinnen',
    },
    testimonialQuote: '„Es ist lebensverändernd … ich spare 5–10 Minuten in jeder einzelnen Konsultation, 10–20 Mal am Tag"',
    testimonialAttr: 'Paul Irvine-Smith – Tierarzt und Klinischer Direktor, Witterings Vets',
    testimonialName: 'Paul Irvine-Smith',
    testimonialRole: 'Tierarzt und Klinischer Direktor, Witterings Vets',
    diagramHeading: 'Ausgaben halbieren, Leistung verdoppeln',
    diagramSub: 'Tauschen Sie Ihr Flickwerk aus Einzellösungen gegen ein integriertes Ökosystem.',
    enterpriseHeading: 'Enterprise oder größere Gruppe?',
    enterpriseCta: 'Kontaktieren Sie uns',
    enterpriseDesc: 'Nehmen Sie Kontakt auf, um individuelle Preise, größere Funktionen und dedizierten Support zu besprechen - vom Onboarding bis zur optimalen Nutzung von Lupa.',
    logoCloudHeading: 'Schließen Sie sich den Kliniken an, die bereits vom Wechsel profitieren',
  },
  enterprise: {
    heroHeading1: 'Tierärztliches',
    heroHeading2: 'Betriebssystem für Unternehmen',
    heroSubtitle:
      'Die weltweit erste Unternehmenslösung für Tierarztgruppen – von zentraler Steuerung bis zur Konfiguration auf Einzelklinikebene',
    heroCta: 'Ihr individueller Plan →',
    responseLabel: 'Reaktionszeit',
    responseUnit: 'Min',
    ratingLabel: 'Capterra-Bewertung',
    uptimeLabel: 'Garantierte Verfügbarkeit',
    featHeading: 'Optimiert für Wachstum, Sicherheit und Erfolg',
    featSub: 'Geben Sie Ihrem Team die Unterstützung, Tools und den Schutz, die sie für den Erfolg brauchen',
    support: {
      title: 'Enterprise-Support',
      b1: 'Dediziertes Enterprise-Erfolgsteam & Account Manager.',
      b2: 'Menschlicher + KI-Support rund um die Uhr verfügbar.',
      b3: 'Maßgeschneiderte Onboarding-Pläne für große Gruppen.',
      b4: 'Erstklassige SLAs mit garantierten Reaktionszeiten.',
    },
    growth: {
      title: 'Für Wachstum gemacht',
      b1: 'Neue Standorte in wenigen Minuten einrichten – mit vorkonfigurierten Einstellungen für Dienste, Preise und Abläufe.',
      b2: 'KI-gestützte Datenmigration sorgt für präzise Übertragung bestehender Klinikunterlagen in 12 Stunden.',
      b3: 'Individuelle Markenführung für einzelne Kliniken möglich.',
      b4: 'Einfache Expansion in neue Regionen mit zentralen Vorlagen und Konfigurationen.',
    },
    security: {
      title: 'Bankgradue Sicherheit',
      b1: 'SOC 2 Typ II zertifiziert und DSGVO-konform.',
      b2: 'Vollständige Prüfprotokolle für Enterprise-Compliance.',
      b3: 'Single Sign-On (SSO) und rollenbasierter Zugriff.',
      b4: 'Bankengrade Verschlüsselung (TLS 1.2 bei Übertragung, AES 256-Bit bei Speicherung).',
      link: 'Mehr zur Sicherheit',
    },
    hierarchyHeading: 'Enterprise-Komplexität einfach verwalten',
    hierarchySub:
      'Etablieren Sie zentrale Konsistenz und entscheiden Sie, wo Flexibilität auf Gruppen-, Klinik- und Abteilungsebene gilt.',
    tabs: {
      enterprise: 'Enterprise-Ebene',
      regional: 'Regionale Ebene',
      clinic: 'Klinikebene',
      department: 'Abteilungsebene',
    },
    enterpriseTab: {
      description:
        'Die höchste Ebene, die alle Regionen überwacht und gruppenweite Richtlinien, Standards und Kontrollen festlegt.',
      b1: 'Referenzdienste, Bestandslisten und Preise sind über alle Standorte standardisiert',
      b2: 'Unternehmensweite Updates werden sofort an alle Kliniken übertragen',
      b3: 'Globale Regeln für Abrechnung, Rabatte und Protokolle werden zentral definiert',
      b4: 'Compliance-, Sicherheits- und Governance-Standards werden global angewendet',
    },
    regionalTab: {
      description: 'Große Regionen oder Filialen, die mehrere Kliniken im selben Gebiet oder unter derselben Marke koordinieren.',
      b1: 'Regionalmanager haben Zugang zu finanziellen, operativen und klinischen KPIs aller Kliniken',
      b2: 'Kliniken derselben Region können klinische Daten und Kundendaten für Überweisungen teilen',
      b3: 'Dienstleistungslisten, Lagerbestände und Berechtigungen werden sofort in der Gruppe angepasst',
      b4: 'Drittanbieter-Tools werden einmal integriert und in der gesamten Region genutzt',
    },
    clinicTab: {
      description: 'Der einzelne tierärztliche Standort, an dem das Team Arbeitsabläufe innerhalb der festgelegten Parameter anpassen kann.',
      b1: 'Hauptsitz definiert den Berechtigungsrahmen für Klinikautonomie',
      b2: 'Kliniken passen Bestandslisten, Angebote, Dienstpläne und Kommunikationsmethoden im Rahmen der Vorgaben an',
      b3: 'Kundendaten und medizinische Akten sind zugänglich und innerhalb und optional zwischen Kliniken synchronisiert',
      b4: 'Sprach-, Währungs- und Steuereinstellungen für einzelne Standorte anwendbar',
    },
    departmentTab: {
      description:
        'Abteilungen innerhalb einer Klinik (z.B. Überweisungsteams, Tag-/Nachtschichten) für tiefere Anpassung bestimmter Rollen.',
      b1: 'Abteilungen erstellen eigene Dienste, Termintypen und Vorlagen',
      b2: 'Workflows, Erinnerungen und Dokumentation für spezifische Teams angepasst',
      b3: 'Berechtigungen und Zugangsstufen pro Abteilung unterschiedlich festgelegt',
      b4: 'Teamspezifisches Terminmanagement, Planung und Online-Buchung aktiviert',
    },
    readyHeading: 'Führen Sie Ihre Gruppe wie eine einzige Praxis.',
    readySub:
      'Individuelle Preise, größere Funktionen und ein dediziertes Team - vom Onboarding bis zu Ihrer hundertsten Klinik.',
    readyCta1: 'Angebot anpassen →',
    readyCta2: 'Alle Funktionen ansehen →',
    readyBadge: '+ GRUPPENPLAN',
    readySpecialist: 'Spezialisierter Support & dedizierter Account Manager',
    readySetup: 'Schnelle Einrichtung - Kliniken in 12 Stunden live',
    readyControl: 'Zentrale Steuerung über alle Standorte hinweg',
    readySecurity: 'Bankengrade Sicherheit · SOC 2 · DSGVO',
  },
  migrations: {
    heroHeading: 'Zu Lupa wechseln',
    heroSubtitle: 'Lupa übernimmt Ihr Onboarding mit Schnelligkeit, Genauigkeit und voller Unterstützung – damit der Wechsel einfach ist.',
    heroCta: 'Mehr erfahren',
    reliabilityHeading: 'Die zuverlässigste Datenmigration auf dem Markt',
    reliabilitySub: 'Vollständig, schnell und mit größter Sorgfalt durchgeführt – vertrauen Sie Ihre Daten unserem Expertenteam an.',
    complete: {
      title: 'Vollständige Migration',
      body: 'Wir gehen von Anfang an die Extra-Meile. Als einziges PMS, das alle Finanz- und Krankenakten überträgt, sorgen wir dafür, dass beim Upgrade nichts verloren geht.',
    },
    accuracy: {
      title: 'Genauigkeit',
      body: 'Von Experteningenieuren überprüft und durch 100+ Kontrollpunkte verbessert. Wir setzen den Branchenmaßstab für Gründlichkeit und Zuverlässigkeit.',
    },
    experience: {
      title: 'Erfahrung',
      body: 'Wir stellen nur die Besten ein. Unsere Dateningenieure haben jahrelange Erfahrung im veterinärmedizinischen Bereich und verstehen Ihre Daten.',
    },
    integrationsHeading: 'Integrieren Sie sich mit Ihren bevorzugten Tools',
    integrationsSub: 'Integriert mit allem Wesentlichen – direkt einsatzbereit.',
    testimonialsHeading: 'Migrationen, die für sich sprechen',
    prevAriaLabel: 'Vorherige Migrationsbewertung',
    migrationQuotes: [
      'Nichts ist je zu viel Aufwand, sie waren immer für uns da, wenn wir Hilfe brauchten',
      'Wir hatten einen großartigen Support von euch… die gesamte Schulung im Vorfeld hat einen riesigen Unterschied gemacht',
      'Es war definitiv einer der einfachsten und reibungslosesten Übergänge, die wir hatten… und ich habe in meiner Karriere schon einige erlebt',
      'Was Integrationen angeht, ist Lupa bisher die beste, und der Support von dir und deinem Team war fantastisch',
      'Eine meiner größten Sorgen war die Datenübertragung… die Ingenieure waren wirklich hilfsbereit',
    ],
    gdprCompliantLabel: 'KONFORM',
    nextAriaLabel: 'Nächste Migrationsbewertung',
    securityHeading: 'Ihre Daten, geschützt',
    securityTab1: 'Datensicherheit & Datenschutz',
    securityTab2: 'Infrastruktur & Zuverlässigkeit',
    securityTab3: 'DSGVO',
    securityBody1:
      'Bei Lupa ist der Schutz Ihrer Klinkdaten nicht verhandelbar. Wir sind SOC 2 und DSGVO-konform, mit bankengrader Verschlüsselung (TLS 1.2 bei Übertragung, AES 256-Bit bei Speicherung). Admin-Kontrollen und detaillierte Prüfprotokolle stellen sicher, dass nur die richtigen Personen auf die richtigen Daten zugreifen.',
    securityBody2:
      'Ihre Daten werden alle 2 Minuten gesichert und in Ihrer Region auf sicherer, skalierbarer AWS-Infrastruktur gehostet – derselben vertrauenswürdigen Plattform, die von führenden globalen Organisationen genutzt wird.',
    securityBody3:
      'Lupa hält führende internationale Datenschutzstandards ein, einschließlich der DSGVO. Ihre Klinkdaten bleiben sicher, privat und konform – egal wo Sie tätig sind.',
    securityBtn: 'Unsere Sicherheitsmaßnahmen',
    onboardingHeading: 'Lupa-Onboarding, Schritt für Schritt',
    onboardingSub: 'Unser Spezialistenteam begleitet Sie Schritt für Schritt, in Ihrem eigenen Tempo.',
    steps: {
      planning: {
        title: 'Planung',
        duration: '1 Tag',
        body: 'Lernen Sie Ihren persönlichen Onboarding-Berater kennen. Wir erfahren mehr über Ihre Arbeitsabläufe, Werte und Teamdynamik, um Lupa auf Sie zuzuschneiden.',
      },
      config: {
        title: 'Konfiguration',
        duration: '4–8 Wochen',
        body: 'Wir bereinigen Ihre Daten, richten Ihr System ein und begleiten die Integration mit Ihren vertrauten Partnern, um Lupa an Ihre Arbeitsabläufe anzupassen.',
      },
      training: {
        title: 'Schulung',
        duration: '2–4 Wochen',
        body: 'Ihr Team erhält eine umfassende Schulung in unserer Sandbox-Umgebung, durch geführte Sitzungen und Selbstlernübungen. Ihr Erfolg ist unser Auftrag.',
      },
      golive: {
        title: 'Go-Live-Unterstützung',
        duration: '2 Tage (Migrationskliniken)',
        body: 'Ihr Berater ist am Go-Live-Tag persönlich vor Ort, um Sie und Ihr Team zu unterstützen und einen reibungslosen Start mit minimalem Stress zu gewährleisten.',
      },
      hypercare: {
        title: 'Nachbetreuung',
        duration: '3 Wochen',
        body: 'Das meiste Lernen passiert in der Praxis. Drei Wochen nach dem Launch bieten wir praxisnahe Unterstützung, damit Ihr Team Vertrauen gewinnt.',
      },
      ongoing: {
        title: 'Laufender Support',
        duration: 'Unbegrenzt',
        body: 'Unser Customer-Success-Team meldet sich proaktiv, verfolgt Ihre Ziele und stellt sicher, dass Lupa mit Ihren Bedürfnissen wächst – mit einer Reaktionszeit von 2 Min. 35 Sek. und einer Kundenzufriedenheit von 98,2 %.',
      },
    },
    faqHeading: 'Ihre Fragen beantwortet',
    faqSub: 'Brauchen Sie mehr Informationen?\nSchreiben Sie uns einfach eine Nachricht und wir melden uns bei Ihnen.',
    faq: [
      {
        question: 'Ist jemand am Go-Live-Tag bei mir?',
        answer:
          'Für migrierte Kliniken: Ja – Ihr dedizierter Onboarding-Berater ist persönlich am Go-Live-Tag vor Ort, um Sie und Ihr Team zu unterstützen. Für neue Praxen ist der Vor-Ort-Support am Go-Live nicht standardmäßig enthalten, kann aber als zusätzlicher bezahlter Service gebucht werden.',
      },
      {
        question: 'Mit wem integriert sich Lupa?',
        answer:
          'Lupa integriert sich mit allen wichtigen Partnern, auf die eine Tierarztpraxis angewiesen ist. Für Labourdiagnostik verbinden wir uns mit IDEXX und Zoetis. Für Apothekenbestellungen integrieren wir NVS und MWI. Für Versicherungsansprüche arbeiten wir mit Vetenvoy. Wir unterstützen auch PACS-Integration für Bildgebung sowie Stripe, Dojo, Outlook, Gmail und mehr.',
      },
      {
        question: 'Was ist eine offene API und was bedeutet das für meine Praxis?',
        answer:
          'Eine offene API ist eine Möglichkeit für verschiedene Softwaresysteme, miteinander zu kommunizieren. In der Praxis bedeutet dies, dass Lupa sich mit Tools und Diensten über unsere bestehenden Integrationen hinaus verbinden kann – ob Überweisungsplattform, Buchhaltungssoftware oder spezifische Anforderungen Ihrer Klinik.',
      },
      {
        question: 'Wie lange dauert der Wechsel zu Lupa?',
        answer:
          'Der vollständige Onboarding-Prozess dauert in der Regel 6–12 Wochen von Anfang bis Ende. Das umfasst eine Planungssitzung, 4–8 Wochen Datenmigration und Systemkonfiguration, 2–4 Wochen Teamschulung und dann den Go-Live mit intensiver Nachbetreuung für 3 Wochen.',
      },
      {
        question: 'Verliere ich beim Wechsel von meinem aktuellen PMS Daten?',
        answer:
          'Nein. Lupa ist das einzige PMS, das alle Finanz- und Krankenakten bei der Migration überträgt. Unsere spezialisierten Dateningenieure überprüfen jede Migration durch 100+ Qualitätskontrollpunkte, um sicherzustellen, dass nichts verloren geht oder korrumpiert wird.',
      },
      {
        question: 'Welche Unterstützung erhalte ich nach dem Go-Live?',
        answer:
          'Sie werden nach dem Launch nicht allein gelassen. Sie erhalten 3 Wochen intensive Nachbetreuung – praxisnahe Unterstützung, während Ihr Team Vertrauen in das System gewinnt. Danach meldet sich unser Customer-Success-Team proaktiv, und unser Support-Team ist mit einer durchschnittlichen Reaktionszeit von unter 3 Minuten und einem Zufriedenheitswert von 98,2 % für Sie da.',
      },
      {
        question: 'Kann ich von jedem Praxisverwaltungssystem zu Lupa wechseln?',
        answer:
          'Ja. Das Migrationsteam von Lupa hat Erfahrung mit der Datenübertragung von einer Vielzahl von Tierarzt-PMS-Plattformen. Unsere Dateningenieure sind auf veterinärmedizinische Daten spezialisiert und verstehen die Feinheiten verschiedener Systeme.',
      },
    ],
  },
  notes: {
    heroHeading1: 'Aufzeichnungen transkribieren & zusammenfassen',
    heroHeading2: 'Mühelos mit Lupa Notes',
    heroSubPre: 'Ihre KI-Investition, die zusätzliche ',
    heroSubHighlight: '27.000 € pro Tierarzt und Jahr',
    heroSubPost: ' generiert und die Mitarbeiterzufriedenheit und Produktivität spürbar steigert.',
    heroCta: 'Kontakt aufnehmen',
    stepsHeading: 'Lupa Notes ist die Zukunft der Veterinärmedizin',
    stepsSub: 'Unser Ziel ist es, die Veterinärbranche dauerhaft umzugestalten, indem wir vielbeschäftigten Tierärzten einfach zu bedienende Tools geben.',
    steps: [
      {
        label: 'Aufnehmen',
        description:
          'Erfassen Sie jeden Moment der Konsultation – von Anfang bis Ende auf Ihrem Telefon oder Computer. Bleiben Sie vollständig auf Ihr Tier und den Halter konzentriert, während die Konsultation im Hintergrund transkribiert wird.',
      },
      {
        label: 'Zusammenfassen',
        description:
          'Wir filtern das Unwichtige heraus und verwandeln jedes Gespräch in klare, präzise medizinische Notizen. Wählen Sie eine Vorlage oder erstellen Sie Ihre eigene – Lupa formatiert sie sofort für Sie.',
      },
      {
        label: 'Aufgabenliste',
        description:
          'Verlieren Sie nie den Überblick über ausstehende Aufgaben. Erstellen Sie automatisch Aufgabenlisten – von Folgenanrufen bis zur Terminvereinbarung für eine Überweisung. Bleiben Sie organisiert und halten Sie jedes Versprechen.',
      },
      {
        label: 'Erweitern',
        description:
          'Möchten Sie mehr einbeziehen? Fügen Sie Sprachnotizen hinzu, laden Sie Überweisungsschreiben, Krankengeschichte oder Labrergebnisse mit Ihrer Aufnahme hoch. Lupa bindet wichtige medizinische Dokumente in Ihre Zusammenfassungen ein.',
      },
      {
        label: 'Exportieren',
        description:
          'Überarbeiten Sie Ihre Notizen manuell oder mit Hilfe unseres KI-Agenten Jerry, bevor Sie sie in Ihr PMS hochladen. Erstellen Sie schließlich kundenfreundliche Zusammenfassungen für Ihre Kunden – alles mit einem Klick.',
      },
    ],
    benefitsHeading1: 'Ein einfacher Wechsel,',
    benefitsHeading2: 'unzählige Vorteile',
    benefitsSub: 'Tausende von Tierärzten sparen täglich 60 Minuten mit Lupa Notes. Sie auch.',
    benefitsCta: 'Kostenlose Testversion buchen',
    benefit1Title: 'Täglich 60 Minuten pro Tierarzt sparen',
    benefit1Desc: 'Schaffen Sie Zeit für Betrieb, Berichterstattung und Patientenversorgung, indem Sie zeitaufwendige manuelle Verwaltungsaufgaben automatisieren.',
    benefit2Title: 'Bessere Kundenerfahrung & Bindung',
    benefit2Desc: 'Verbringen Sie weniger Zeit am Tippen und mehr Zeit mit Kunden. Erstellen Sie Konsultationszusammenfassungen, damit Kunden informiert und im Einklang mit dem Plan bleiben.',
    benefit3Title: 'Mehr Umsatz erfassen, mühelos',
    benefit3Desc: 'Steigern Sie den Umsatz, indem Sie Zeit für Zusatzleistungen wie Krallenschneiden und Bluttests freischaufeln. Lupas KI hebt verpasste Abrechnungspositionen direkt aus Ihren Notizen hervor.',
    worksWith: 'Nutzen Sie Lupa Notes auf Ihre Art',
    worksWithOption1Title: 'Im Paket enthalten',
    worksWithOption1Desc: 'Lupa Notes ist bereits in Ihr Lupa-OS-Abonnement integriert.',
    worksWithOption2Title: 'Funktioniert mit jedem PMS',
    worksWithOption2Desc: 'Noch kein Lupa OS? Kein Problem. Lupa Notes funktioniert nahtlos mit jedem PMS – fangen Sie sofort an, Zeit zu sparen.',
    testimonialsHeading1: 'Vertrauen von Kliniken',
    testimonialsHeading2: 'aus der gesamten Branche',
    prevAriaLabel: 'Vorherige Bewertung',
    nextAriaLabel: 'Nächste Bewertung',
    notesTestimonials: [
      {
        designation: 'Tierarzt, Woodstock',
        quote: '„Ich glaube, es spart mir an einem normalen Tag etwa eine Stunde. An einem hektischen Tag noch mehr… Kunden müssen nicht warten, das verbessert den NPS, was momentan wirklich wichtig ist."',
      },
      {
        designation: 'Direktor, Witterings Vets',
        quote: '„Es ist lebensverändernd… ich habe wieder Freude daran, Tierarzt zu sein, mich um meine Patienten zu kümmern, statt klinische Notizen zu schreiben."',
      },
      {
        designation: 'Praxisinhaberin, Burghley Veterinary Centre',
        quote: '„Euer KI-Transkriptionstool ist für uns ein absoluter Gamechanger! Ich kann nicht glauben, wie schnell es sich verbessert und weiterentwickelt!"',
      },
      {
        designation: 'Tierärztin, Derbyshire',
        quote: '„Ich bin weiterhin voll im Gespräch und höre aufmerksam zu, aber es gibt mir die Sicherheit, dass auch wenn ich nicht alles erinnere oder vollständig verarbeite, am Ende alles im Transkript und in den Notizen steht."',
      },
      {
        designation: 'Tierarzt, Coventry',
        quote: '„Es ist eine glasklare Entscheidung. Es ist fast peinlich, wie viel Zeit ich pro Tag spare… ich schaffe Konsultationen wieder in 10 Minuten statt 15."',
      },
      {
        designation: 'Tierärztlicher Leiter, Pet People',
        quote: '„Meine persönliche Erfahrung: Das ist großartig. Ich genieße Konsultationen viel mehr. Ich denke nicht mehr daran, was ich notieren muss, und unterbreche das Gespräch nicht, um wichtige Infos aufzuschreiben."',
      },
    ],
    faqHeading: 'Ihre Fragen beantwortet',
    faq: [
      {
        question: 'Sind meine Daten bei Lupa Notes sicher?',
        answer:
          'Ja. Lupa Notes ist mit Datensicherheit und Datenschutz im Kern aufgebaut. Wir sind DSGVO-konform, SOC 2 Typ II zertifiziert und nutzen TLS 1.2 bei Übertragung sowie AES 256-Bit-Verschlüsselung bei Speicherung. Ihre Aufnahmen und Notizen werden sicher gespeichert und nie an Dritte weitergegeben.',
      },
      {
        question: 'Muss ich vor der Aufnahme einer Konsultation die Einwilligung des Kunden einholen?',
        answer:
          'Ja, Sie sollten immer die Einwilligung Ihres Kunden einholen, bevor Sie mit der Aufnahme beginnen. Wir machen dies einfach mit fertigen Einwilligungsabläufen und Praxisbeschilderungsvorlagen. Die meisten Kunden stimmen zu, sobald sie den Nutzen verstehen.',
      },
      {
        question: 'Speichert Lupa Notes die Audioaufnahmen meiner Konsultationen?',
        answer:
          'Nein, Lupa Notes speichert Ihre Audioaufnahmen nicht dauerhaft. Die Audiodaten werden vorübergehend zur Verarbeitung und Genauigkeitsprüfung gespeichert und dann gelöscht. Nur die strukturierten Textnotizen und Zusammenfassungen werden in Ihren Unterlagen gespeichert.',
      },
      {
        question: 'Wie viel Zeit spart Lupa Notes Tierärzten tatsächlich?',
        answer:
          'Tierärzte, die Lupa Notes nutzen, sparen durchschnittlich 60 Minuten pro Tag bei der klinischen Dokumentation und Verwaltung. Über ein Jahr hinweg, für eine Klinik mit 3 Tierärzten, bedeutet das erheblich zurückgewonnene Zeit – Zeit, die in Konsultationen, Operationen oder einfach pünktlichen Feierabend reinvestiert werden kann.',
      },
      {
        question: 'Sind die KI-generierten Notizen genau?',
        answer:
          'Ja – aber Sie haben immer die Kontrolle. Lupa Notes erfasst das vollständige Konsultationsprotokoll und generiert strukturierte Notizen basierend auf Ihrer Vorlage. Bevor etwas in der Patientenakte gespeichert wird, überprüfen und genehmigen Sie die Notizen.',
      },
      {
        question: 'Kann ich das Format der von Lupa Notes generierten Notizen anpassen?',
        answer:
          'Ja. Lupa Notes ermöglicht es Ihnen, aus vorhandenen Notizvorlagen zu wählen oder Ihr eigenes individuelles Format zu erstellen. Sie können unterschiedliche Vorlagen pro Tierart, Konsultationstyp oder klinischem Team haben – volle Kontrolle über die Struktur Ihrer Unterlagen.',
      },
    ],
  },
  team: {
    heroHeading1: 'Helfen Sie uns,',
    heroHeading2: 'die weltweite Plattform für Tiergesundheit aufzubauen',
    heroSubtitle: 'Treten Sie Lupa bei und hinterlassen Sie Ihren Abdruck in der weltweiten Veterinärmedizin',
    heroCta: 'Jetzt bewerben',
    europeHeading: "Europas #1 Start-up-Team",
    europeSub: 'Unsere Mitarbeiter kommen von den besten Technologie- und Strategieorganisationen der Welt.',
    valuesHeading: 'Die Werte, die uns definieren',
    valuesSub: 'Das Fundament unseres Unternehmens und unserer Kultur.',
    values: [
      { title: 'Vertrauen Sie Ihrem Instinkt', desc: 'Wir vertrauen Ihnen, mit Überzeugung zu führen, auf Intuition und Erfahrung zu handeln und Ihre Entscheidungen zu verantworten.' },
      { title: 'Verlieben Sie sich täglich neu', desc: 'Wir ermutigen Sie, Ihre Rolle zu gestalten, damit Sie in Ihrer Arbeit täglich Sinn und Erfüllung finden.' },
      { title: 'Behandeln Sie es wie Ihr Eigenes', desc: 'Wir glauben an echte Eigenverantwortung – sich selbst verantwortlich zu halten, mit Stolz zu liefern und zu wissen, dass Ihr Einsatz anerkannt wird.' },
      { title: 'Handeln Sie mit extremer Dringlichkeit', desc: 'Wir bewegen uns schnell und sorgfältig, mit Absicht. Jede Entscheidung und Maßnahme treibt echte Auswirkungen in diesem Unternehmen voran.' },
      { title: 'Seien Sie mutig und bauen Sie die Zukunft', desc: 'Bei Lupa fördern wir mutiges Denken und frische Ideen – wir verschieben Grenzen, um Lösungen zu bauen.' },
    ],
    gridHeading: 'Das Team hinter Lupa',
    gridSub: 'Lernen Sie einige der Menschen kennen, die unser Produkt gestalten.',
    formerly: 'Zuvor',
    headcountHeading: 'Kleines Team, Große Wirkung',
    headcountSub: 'Werde Teil unseres sorgfältig ausgewählten Teams, das mit Vertrauen und Rückhalt etwas Wirkungsvolles aufbaut.',
    eliteEngineers: 'Elite-Ingenieure',
    raised: 'Bisher gesammelt',
    petsRegistered: 'Registrierte Haustiere',
    softwareTab: 'Software-Ingenieure',
    dataTab: 'Daten-Ingenieure',
    productTab: 'Produktteam',
    clientTab: 'Kundenteam',
    showLess: 'Weniger anzeigen',
    showAll: 'Alle anzeigen',
    principlesEyebrow: 'Engineering',
    principlesHeading1: 'Prinzipien, die unser',
    principlesHeading2: 'Ingenieurteam antreiben',
    principlesSub: 'Wir haben unsere Ingenieurkultur auf den wichtigsten Lektionen aufgebaut, die unser Team bei den weltweit führenden Technologieunternehmen gelernt hat.',
    principles: [
      {
        principle: 'Skalierbar bauen',
        quote: 'Wir bauen Systeme, die Bestand haben. Wir setzen auf solide Grundlagen, sinnvolles Testing und kluge Technologieentscheidungen, die uns heute schnell halten und für morgen rüsten.',
      },
      {
        principle: 'Schnell ausliefern',
        quote: 'Wir bringen Ideen schnell zum Leben. Ingenieure bauen, deployen und sehen unmittelbar den Impact ihrer Arbeit.',
      },
      {
        principle: 'Eigenverantwortung',
        quote: 'End-to-End-Verantwortung ist zentral für unsere Arbeitsweise. Bei Lupa beginnt das Lösen echter Probleme mit Autonomie und wird durch Zusammenarbeit gestärkt.',
      },
      {
        principle: 'Kundenbesessen',
        quote: 'Wir bleiben in engem Kontakt mit den Kliniken – ihr Feedback prägt, was wir bauen und wie wir es verfeinern.',
      },
      {
        principle: 'Die Besten einstellen',
        quote: 'Wir glauben an den Wert, mit Top-Talenten zu bauen. Wir legen die Latte bewusst hoch, denn mit den Besten zu arbeiten ist der Weg, schneller zu wachsen und besser zu bauen.',
      },
    ],
    techHeading: 'Unser modernster Tech-Stack',
    techFrontend: 'Frontend',
    techBackend: 'Backend',
    techDatabase: 'Datenbank',
    techDataAnalytics: 'Daten & Analytik',
    techMobile: 'Mobile App',
    benefitsHeading: 'Ihre Vorteile bei Lupa',
    benefitsSub: 'Wir investieren in unser Team, damit es in Lupa investieren kann (im wahrsten Sinne!).',
    benefits: [
      { title: 'Außergewöhnliche Vergütung', desc: 'Top-Vergütungspaket im Markt, um die besten Talente zu gewinnen und zu halten.' },
      { title: 'Unternehmensanteile für alle', desc: 'Jeder Mitarbeiter erhält Anteile - keine Ausnahmen, alle profitieren vom Wachstum.' },
      { title: '25 Urlaubstage', desc: '25 Urlaubstage pro Jahr zum Erholen, Aufladen und Reisen.' },
      { title: 'Visum / Umzugshilfe', desc: 'Visum-Sponsoring mit komplettem Umzugspaket für den Start in London.' },
      { title: 'Private Krankenversicherung', desc: 'Erstklassige private Krankenversicherung für Sie und Ihre Sicherheit.' },
      { title: 'MacBook Air M4', desc: 'Neuestes MacBook Air M4 plus alle Produktivitäts- und KI-Tools, die Sie brauchen.' },
      { title: 'Jährliche Teamreise', desc: 'Jährliche Teamreise und alle zwei Wochen Team-Socials - wir arbeiten und erleben gemeinsam.' },
      { title: 'Büro im Zentrum Londons', desc: 'Hochwertiges Büro im Herzen Londons, ausgelegt auf Konzentration und Zusammenarbeit.' },
      { title: 'Kostenloser Kaffee & Bier', desc: 'Kostenloser Barista-Kaffee und Bier vom Hahn, um die Energie hochzuhalten.' },
      { title: 'Subventioniertes Fitnessstudio', desc: 'Subventionierte Fitnessstudio-Mitgliedschaft, damit Fitness in Ihren Alltag passt.' },
    ],
  },
  contact: {
    tabDemo: '📋 Demo buchen',
    tabMessage: '✏️ Nachricht senden',
    demoHeading: '🚀 Lupa in Aktion erleben',
    demoSub: 'Buchen Sie eine Demo, um zu sehen, wie Lupa Ihre Klinik transformieren kann.',
    demoQuote: 'Jeder Mensch, dem wir bei Lupa begegnet sind, war erstaunlich – unterstützend, hilfsbereit und beeindruckend klug.',
    demoQuoteAttr: '- Natasha, Polden Hills Veterinary Centre',
    msgHeading: '🚀 Wir sind für Sie da',
    msgSub: 'Haben Sie Fragen? Schreiben Sie uns und wir melden uns innerhalb von 4 Werksunden bei Ihnen.',
    msgQuote: 'Lupa war bisher das Beste, und der Support von Ihnen und dem Team war großartig!',
    msgQuoteAttr: '- Tierarzt, Norwich',
    trustedBy: 'Weltweit von Kliniken vertraut',
    namePlaceholder: 'Ihr vollständiger Name',
    nameLabel: 'Name',
    emailLabel: 'E-Mail',
    emailPlaceholder: 'sie@ihreklinik.de',
    companyLabel: 'Firmenname',
    companyPlaceholder: 'Ihre Klinik oder Praxis',
    locationLabel: 'Wo befinden Sie sich?',
    locationSelect: 'Auswählen...',
    submitBtn: 'Absenden',
    successTitle: 'Anfrage erhalten!',
    successSub: 'Unser Team meldet sich innerhalb von 4 Werksunden bei Ihnen.',
    fullName: 'Vollständiger Name*',
    emailAddr: 'E-Mail-Adresse*',
    phone: 'Telefonnummer',
    company: 'Unternehmen',
    message: 'Nachricht',
    privacyLabel: 'Ich habe die ',
    privacyLink: 'Datenschutzrichtlinie*',
    termsLabel: ' und die ',
    termsLink: 'AGB*',
    sendBtn: 'Nachricht senden',
    msgSuccessTitle: 'Nachricht gesendet!',
    msgSuccessSub: 'Wir melden uns innerhalb von 4 Werksunden bei Ihnen.',
    requiredField: 'Dieses Feld ist erforderlich',
    privacyRequired: 'Bitte akzeptieren Sie die Datenschutzrichtlinie und AGB',
  },
  blog: {
    heroHeading: 'Der Lupa-Blog',
    heroSub: 'Einblicke in Praxismanagement, KI und das Wachstum Ihrer Klinik',
    latest: 'Alle',
    allCategories: 'Alle',
    readMore: 'Mehr lesen',
    techAI: 'Tech & KI',
    lupaNews: 'Lupa News',
    businessInsights: 'Business-Einblicke',
    videos: 'Videos',
    backToBlog: 'Zurück zum Blog',
    noPostsFound: 'Keine Beiträge gefunden.',
    jumpToSection: 'Zum Abschnitt springen',
    shareArticle: 'Artikel teilen',
    joinMailing: 'Newsletter abonnieren',
    mailingDesc: 'Bleiben Sie über Lupa-Neuigkeiten und Sonderangebote auf dem Laufenden.',
    emailPlaceholder: 'Ihre E-Mail-Adresse',
    subscribe: 'Abonnieren',
    seeLupa: 'Lupa in Aktion erleben',
    book30min: 'Buchen Sie eine 30-minütige Demo mit unserem Team.',
    bookDemo: 'Demo buchen',
    latestBlogs: 'Neueste Beiträge',
    backToAll: 'Zurück zu allen Beiträgen',
    inlineCta: 'Bereit, 60+ Min. pro Tierarzt täglich zu sparen?',
    inlineSub1: 'Lupa hilft ',
    inlineSub2: 'Ihrer Praxis',
    inlineSub3: ', sich auf ',
    inlineSub4: 'allen Ebenen',
    letsTalk: 'Sprechen wir',
    hubHeading: 'Blog & Erfolgsgeschichten',
    hubLede: 'Praxisnahe Einblicke für Inhaber und Leitungen von Tierarztpraxen.',
    featuredLabel: 'Empfohlener Beitrag',
    allArticlesLabel: 'Alle Beiträge',
    readArticle: 'Beitrag lesen',
    previous: 'Zurück',
    next: 'Weiter',
    pageLabel: 'Seite',
    paginationLabel: 'Seitennavigation',
    keyPoints: 'Kernpunkte',
    breadcrumbBlog: 'Blog',
    clinicalReview: 'Fachliche Prüfung',
    clinicallyReviewed: 'Fachlich geprüft',
    faqHeading: 'Häufig gestellte Fragen',
    writtenBy: 'Geschrieben von',
    readMoreAbout: 'Mehr über',
    keepReading: 'Weiterlesen',
    aboutAuthor: 'Über %s',
    articlesBy: 'Beiträge von %s',
  },
  cma: {
    heading: 'Schützen Sie Ihr Unternehmen und Team vor den CMA-Vorschriften',
    sub: 'Haben Sie unser Webinar verpasst? Sehen Sie die Aufzeichnung – kostenlos bis zum 30. November',
    watchBtn: 'Hier ansehen',
  },
  meta: {
    home: {
      title: 'Lupa Pets - KI-gestützte Software für die Tierarztpraxis-Verwaltung',
      description: 'Das weltweit erste enterprise-fähige KI-Betriebssystem für Tierärzte.',
    },
    pricing: {
      title: 'Preise - Lupa Pets',
      description:
        'Transparente, flexible Preise für Tierarztpraxen jeder Größe. Stellen Sie Ihren individuellen Plan zusammen und berechnen Sie Ihren ROI.',
    },
    lupaNotes: {
      title: 'Lupa Notes - KI-Scribe für Tierärzte',
      description:
        'Sparen Sie 60 Minuten pro Tierarzt und Tag mit Lupa Notes, dem KI-Scribe, der Konsultationen automatisch transkribiert und zusammenfasst.',
    },
    migrations: {
      title: 'Wechsel zu Lupa - Nahtloser PMS-Umstieg für Tierarztpraxen | Lupa Pets',
      description:
        'Professioneller Wechsel der Praxissoftware mit vollständiger Datenmigration, persönlichem Support und nahtloser Integration. SOC-2-konform mit 98,2% Zufriedenheit.',
    },
    contact: {
      title: 'Kontakt - Lupa Pets',
      description: 'Nehmen Sie Kontakt mit dem Lupa-Team auf.',
    },
    team: {
      title: 'Team - Lupa Pets',
      description:
        'Lernen Sie das Team kennen, das die weltweit erste enterprise-fähige KI-Tierarztplattform entwickelt. Ingenieure von Google, Meta, Palantir, Amazon und BCG.',
    },
    enterprise: {
      title: 'Enterprise - Lupa Pets',
      description: 'Das weltweit erste Enterprise-Betriebssystem für Tierärzte.',
    },
    aaha: {
      title: 'AAHA Advantage - Lupa Pets',
      description:
        'Das PIMS für AAHA-akkreditierte Praxen. Sonderpreise, KI-Workflows und klinische Exzellenz - exklusiv für AAHA-Mitglieder.',
    },
    blog: {
      title: 'Blog - Lupa Pets',
      description:
        'Einblicke in Praxismanagement, KI-Technologie, CMA-Vorschriften und das Wachstum Ihrer Klinik.',
    },
    privacyPolicy: {
      title: 'Datenschutz - Lupa Pets',
      description: 'Datenschutzerklärung von Lupa Pets.',
    },
    dataProtectionComplaints: {
      title: 'Verfahren für Datenschutzbeschwerden - Lupa Pets',
      description:
        'Wie Sie eine Datenschutzbeschwerde bei Lupa Pets einreichen und wie wir Beschwerden gemäß dem Datenschutzrecht des Vereinigten Königreichs entgegennehmen, prüfen, untersuchen und beantworten.',
    },
    terms: {
      title: 'AGB - Lupa Pets',
      description: 'Allgemeine Geschäftsbedingungen von Lupa Pets.',
    },
  },
}

const TRANSLATIONS: Record<SiteLocale, Translations> = { en, us, es, de }

export function getTranslations(locale: SiteLocale): Translations {
  return TRANSLATIONS[locale]
}

export function useTranslations(): Translations
export function useTranslations<K extends keyof Translations>(section: K): Translations[K]
export function useTranslations<K extends keyof Translations>(section?: K) {
  const locale = useLocale()
  const t = TRANSLATIONS[locale]
  return section ? t[section] : t
}
